import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, AppDispatch } from "../../../redux/store";

/* ← adjust this path to wherever your food slice lives */
import {
  listenToBrandFoodFeed,
  stopBrandFoodFeed,
  upsertFoodItem,
  upsertFoodMedia,
  deleteFoodItem,
  deleteFoodPhoto,
  FoodItem,
} from "../../../redux/foodSlice";

import {
  Plus, Search, X, ChevronLeft, ChevronDown, Image as ImageIcon,
  Trash2, Check, Bold, Italic, Underline, Link2,
  ListOrdered, List as ListIcon, Quote, Undo2, Redo2, AlignLeft,
  GripHorizontal, UtensilsCrossed, Timer, Snowflake, Video, Tag, Scale,
  Ruler, Camera, RotateCcw, ShoppingBasket, Sprout,
} from "lucide-react";

/* ═══════════════════════════════════════════════════════════
   THE ONLY TWO SOURCES OF TRUTH
   Categories come from CategoriesCollection, every field that
   describes an item comes from CategoriesCollectionAttributes.
   Nothing about spice, diet, size or colour is written here.
═══════════════════════════════════════════════════════════ */
import { AllCategoriesFlat, type CategoryItem } from "../../../CATEGORIES/CategoriesCollection";
import {
  attributesForCategory,
  findNodePath,
  ATTR_UNIT,
  ATTR_PACKAGING,
} from "../../../CATEGORIES/ATTRIBUTES/CategoriesCollectionAttributes";
import {
  type RateType,
  DEFAULT_RATE_TYPE,
} from "../../../redux/storeProductServiceFeeSlice";

/* ═══════════════════════════════════════════════════════════
   VERTICALS
   One food business, three counters. Fast food plates it,
   grocery shelves it, the farm pulls it out of the ground.
   Each one browses its own department(s) of the same tree and
   only ever shows the fields that counter actually uses.
═══════════════════════════════════════════════════════════ */
export type Vertical = "fastfood" | "grocery" | "farm";

interface VerticalConfig {
  key: Vertical;
  /** Department ids in CategoriesCollection this counter can list from. */
  roots: string[];
  accent: string;
  accentDark: string;
  accentSoft: string;
  icon: React.ReactNode;
  /** Copy — one vocabulary per counter, used everywhere. */
  item: string;
  items: string;
  newLabel: string;
  liveTab: string;
  liveWord: string;
  searchLabel: string;
  pickLabel: string;
  pickHint: string;
  emptyTitle: string;
  emptyBody: string;
  detailsTitle: string;
  sizesTitle: string;
  sizesHint: string;
  sizeNamePlaceholder: string;
  stockLabel: string;
  /** Optional blocks. */
  showKitchen: boolean;      // prep time + calories
  showExtras: boolean;       // paid add-ons
  showFreshness: boolean;    // shelf life / best before / batch
  showHarvest: boolean;      // made or harvested on
  /** Colour is a universal attribute — only these counters honour it. */
  allowUniversalColor: boolean;
  /** Attributes to guarantee even when the tree doesn't declare them. */
  ensure: any[];
}

const VERTICALS: Record<Vertical, VerticalConfig> = {
  fastfood: {
    key: "fastfood",
    roots: ["08"],
    accent: "#C8202E",
    accentDark: "#9E1521",
    accentSoft: "#FBE7E9",
    icon: <UtensilsCrossed size={15} />,
    item: "dish",
    items: "dishes",
    newLabel: "New dish",
    liveTab: "On the menu",
    liveWord: "On menu",
    searchLabel: "Search your menu",
    pickLabel: "What are you serving?",
    pickHint: "Drill in until it matches the dish — the fields below follow your choice.",
    emptyTitle: "Nothing on the menu yet",
    emptyBody: "Add your first dish and hungry people nearby will see it.",
    detailsTitle: "Dish details",
    sizesTitle: "Sizes",
    sizesHint: "Optional. Name them the way your kitchen does — the price is added on top of the base price.",
    sizeNamePlaceholder: "e.g. Large",
    stockLabel: "Portions left today",
    showKitchen: true,
    showExtras: true,
    showFreshness: false,
    showHarvest: false,
    allowUniversalColor: false,
    ensure: [],
  },
  grocery: {
    key: "grocery",
    roots: ["08"],
    accent: "#E9A116",
    accentDark: "#B87708",
    accentSoft: "#FCEFD4",
    icon: <ShoppingBasket size={15} />,
    item: "product",
    items: "products",
    newLabel: "New product",
    liveTab: "On the shelf",
    liveWord: "On shelf",
    searchLabel: "Search your aisles",
    pickLabel: "What are you stocking?",
    pickHint: "Drill in until it matches the product — the fields below follow your choice.",
    emptyTitle: "No products on the shelf yet",
    emptyBody: "Add your first product and shoppers nearby will see it.",
    detailsTitle: "Product details",
    sizesTitle: "Pack sizes",
    sizesHint: "Optional. One row per pack you sell — the price is added on top of the base price.",
    sizeNamePlaceholder: "e.g. 6-pack",
    stockLabel: "Units in stock",
    showKitchen: false,
    showExtras: false,
    showFreshness: true,
    showHarvest: false,
    allowUniversalColor: true,
    ensure: [ATTR_UNIT, ATTR_PACKAGING],
  },
  farm: {
    key: "farm",
    roots: ["08"],
    accent: "#5AA22B",
    accentDark: "#3B6E1B",
    accentSoft: "#EAF5E0",
    icon: <Sprout size={15} />,
    item: "produce",
    items: "produce",
    newLabel: "New produce",
    liveTab: "At the stall",
    liveWord: "At stall",
    searchLabel: "Search your stall",
    pickLabel: "What did you bring in?",
    pickHint: "Drill in until it matches what you picked — the fields below follow your choice.",
    emptyTitle: "Nothing at the stall yet",
    emptyBody: "Add your first crop and buyers nearby will see it.",
    detailsTitle: "Produce details",
    sizesTitle: "Units of sale",
    sizesHint: "Optional. One row per way you sell it — by the punnet, the crate, the kilo.",
    sizeNamePlaceholder: "e.g. Crate",
    stockLabel: "Available today",
    showKitchen: false,
    showExtras: false,
    showFreshness: true,
    showHarvest: true,
    allowUniversalColor: true,
    ensure: [ATTR_UNIT, ATTR_PACKAGING],
  },
};

/* ═══════════════════════════════════════════════════════════
   FOOD REGISTRY — the one place that says which department and
   which branches this whole screen reads from. Comment out a
   line to switch that counter off everywhere (picker, list,
   vertical detection) without touching anything else in the file.
═══════════════════════════════════════════════════════════ */
const FOOD_DEPARTMENT_ID = "08";

const FOOD_BRANCHES: Partial<Record<Vertical, string>> = {
  fastfood: "0801",
  grocery:  "0802",
  farm:     "0803",
  // bakery: "0804",   // ← example: uncomment + add a matching VERTICALS entry to turn on a 4th counter
};

/** second-level category id under Food, e.g. "0801" / "0802" / "0803" */
const branchOf = (id: string): string => catPath(id)[1]?.id ?? "";

/** Derived — which vertical a branch id belongs to. Edit FOOD_BRANCHES above, not this. */
const BRANCH_VERTICAL: Record<string, Vertical> = Object.fromEntries(
  Object.entries(FOOD_BRANCHES).map(([v, b]) => [b as string, v as Vertical])
);

/** Derived — each vertical's own branch, used as the default scope root. */
const VERTICAL_BRANCH: Record<Vertical, string> = FOOD_BRANCHES as Record<Vertical, string>;

/* ═══════════════════════════════════════════════════════════
   NEUTRAL PALETTE — shared across all three counters. Only the
   accent changes, and it changes through CSS variables.
═══════════════════════════════════════════════════════════ */
const HEART = {
  panelBg:   "#FFFFFF",
  panel2:    "#FFF9EF",
  dark:      "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",
  green:     "#3F8A4C",
  greenSoft: "#E7F3E8",
  gold:      "#F2A93E",
  goldDark:  "#D4900F",
  goldSoft:  "#FCEFD4",
  border:    "#EEDFC4",
  divider:   "#F3E6CE",
};

const FS_CSS = `
  /* --fs-top is the height of the dashboard header this screen lives under.
     --fs-acc / -d / -s are set per vertical on .fs-root, so every accent in
     this sheet re-themes at once. */
  .fs-root{
    font-family:'Montserrat','Cormorant Garamond',sans-serif;
    color:${HEART.dark};
    background:transparent;
    min-height:0;
    overflow:visible;
    padding-bottom:40px;
  }
  .fs-root ::-webkit-scrollbar{width:8px;height:8px;}
  .fs-root ::-webkit-scrollbar-thumb{background:${HEART.border};border-radius:8px;}
  .fs-root button{font-family:inherit;}
  .fs-root input,.fs-root textarea,.fs-root select{font-family:inherit;}

  @keyframes fsPop{from{opacity:0;transform:translateY(8px) scale(.985);}to{opacity:1;transform:none;}}
  @keyframes fsSlide{from{opacity:0;transform:translateX(12px);}to{opacity:1;transform:none;}}
  @keyframes fsSpin{to{transform:rotate(360deg);}}
  @keyframes fsShimmer{0%{background-position:-400px 0;}100%{background-position:400px 0;}}
  @keyframes fsSheetUp{from{transform:translateY(100%);}to{transform:translateY(0);}}
  @keyframes fsFadeIn{from{opacity:0;}to{opacity:1;}}

  .fs-fade{animation:fsPop .22s ease both;}
  .fs-slide{animation:fsSlide .22s ease both;}
  .fs-spin{animation:fsSpin .7s linear infinite;}
  .fs-skeleton{background:linear-gradient(90deg,${HEART.goldSoft} 0%,#fff 50%,${HEART.goldSoft} 100%);background-size:800px 100%;animation:fsShimmer 1.4s linear infinite;}

  @media (prefers-reduced-motion: reduce){
    .fs-fade,.fs-slide,.fs-skeleton,.fs-spin{animation:none !important;}
  }

  .fs-num{color:var(--fs-acc-d);font-weight:800;font-variant-numeric:tabular-nums;letter-spacing:-.01em;}
  .fs-num-lg{font-size:21px;}

  .fs-btn{border:none;border-radius:11px;font-weight:700;cursor:pointer;transition:transform .1s,box-shadow .15s,background .15s,border-color .15s,color .15s;}
  .fs-btn:active{transform:scale(.97);}
  .fs-btn:focus-visible{outline:2px solid var(--fs-acc);outline-offset:2px;}
  .fs-btn-primary{background:linear-gradient(135deg,var(--fs-acc),var(--fs-acc-d));color:#fff;box-shadow:0 6px 16px var(--fs-acc-glow);}
  .fs-btn-primary:hover{box-shadow:0 8px 22px var(--fs-acc-glow);}
  .fs-btn-primary:disabled{opacity:.55;cursor:not-allowed;box-shadow:none;}
  .fs-btn-ghost{background:#fff;color:${HEART.dark};border:1px solid ${HEART.border};}
  .fs-btn-ghost:hover{background:var(--fs-acc-s);border-color:var(--fs-acc);}
  .fs-btn-danger{background:#FBE7E9;color:#9E1521;border:1px solid #FBE7E9;}
  .fs-btn-danger:hover{background:#f6cfd2;}

  .fs-input,.fs-textarea,.fs-select{
    width:100%;border:1px solid ${HEART.border};border-radius:12px;background:#fff;
    padding:11px 13px;font-size:13.5px;color:${HEART.dark};outline:none;transition:border-color .15s,box-shadow .15s;
  }
  .fs-input:focus,.fs-textarea:focus,.fs-select:focus{border-color:var(--fs-acc);box-shadow:0 0 0 3px var(--fs-acc-s);}
  .fs-input::placeholder,.fs-textarea::placeholder{color:${HEART.textFaint};}
  .fs-label{font-size:11px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:${HEART.textMuted};margin-bottom:7px;display:block;}
  .fs-hint{font-size:11.5px;color:${HEART.textFaint};margin-top:7px;}

  .fs-card{background:#fff;border:1px solid ${HEART.border};border-radius:18px;padding:20px;box-shadow:0 3px 12px rgba(27,14,6,.05);}
  .fs-cardtitle{font-size:15px;font-weight:800;color:${HEART.dark};margin-bottom:14px;}
  .fs-cardhead{display:flex;align-items:center;gap:10px;margin-bottom:16px;}
  .fs-cardic{width:30px;height:30px;border-radius:9px;background:var(--fs-acc-s);color:var(--fs-acc-d);display:flex;align-items:center;justify-content:center;flex-shrink:0;}
  .fs-cardic.hot{background:linear-gradient(135deg,var(--fs-acc),var(--fs-acc-d));color:#fff;box-shadow:0 3px 8px var(--fs-acc-glow);}

  .fs-toggle{position:relative;width:38px;height:22px;border-radius:999px;border:none;cursor:pointer;flex-shrink:0;transition:background .15s;background:${HEART.border};}
  .fs-toggle.on{background:linear-gradient(135deg,var(--fs-acc),var(--fs-acc-d));}
  .fs-toggle span{position:absolute;top:2px;left:2px;width:18px;height:18px;border-radius:50%;background:#fff;transition:transform .15s;box-shadow:0 1px 3px rgba(0,0,0,.25);}
  .fs-toggle.on span{transform:translateX(16px);}
  .fs-togglerow{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:11px 0;border-top:1px solid ${HEART.divider};}
  .fs-togglerow:first-of-type{border-top:none;}
  .fs-togglelabel{font-size:13px;font-weight:700;display:flex;align-items:center;gap:8px;}

  .fs-pill{display:inline-flex;align-items:center;gap:5px;font-size:10.5px;font-weight:800;letter-spacing:.03em;padding:5px 10px;border-radius:999px;}
  .fs-pill-live{background:var(--fs-acc-s);color:var(--fs-acc-d);}
  .fs-pill-draft{background:${HEART.goldSoft};color:${HEART.goldDark};}
  .fs-pill-off{background:#F1EDE6;color:${HEART.textMuted};}

  .fs-chips{display:flex;flex-wrap:wrap;gap:8px;}
  .fs-chip{display:inline-flex;align-items:center;gap:6px;background:${HEART.panel2};border:1px solid ${HEART.border};color:${HEART.dark};font-size:12px;font-weight:700;padding:8px 13px;border-radius:999px;cursor:pointer;transition:background .12s,border-color .12s,color .12s,box-shadow .12s;}
  .fs-chip:hover{border-color:var(--fs-acc);background:var(--fs-acc-s);}
  .fs-chip.on{background:linear-gradient(135deg,var(--fs-acc),var(--fs-acc-d));color:#fff;border-color:transparent;box-shadow:0 3px 10px var(--fs-acc-glow);}
  .fs-chip.on .fs-num{color:#fff;}
  .fs-chip-del{display:inline-flex;background:none;border:none;padding:0;cursor:pointer;color:inherit;opacity:.65;}
  .fs-chip-del:hover{opacity:1;}

  .fs-searchbar{display:flex;align-items:center;gap:9px;background:#fff;border:1px solid ${HEART.border};border-radius:999px;padding:9px 15px;}
  .fs-searchbar input{border:none;outline:none;background:transparent;font-size:13px;flex:1;min-width:0;color:${HEART.dark};}

  .fs-topbar{background:#fff;border:1px solid ${HEART.border};border-radius:18px;box-shadow:0 3px 12px rgba(27,14,6,.05);overflow:hidden;margin-bottom:18px;}
  .fs-topbar-a{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:14px 16px;border-bottom:1px solid ${HEART.divider};flex-wrap:wrap;}
  .fs-tabs{display:flex;gap:20px;}
  .fs-tab{background:none;border:none;padding:2px 0;font-size:13px;font-weight:800;color:${HEART.textFaint};cursor:pointer;position:relative;display:flex;align-items:center;gap:6px;}
  .fs-tab.active{color:${HEART.dark};}
  .fs-tab.active::after{content:"";position:absolute;left:0;right:0;bottom:-14px;height:2px;background:var(--fs-acc);border-radius:2px;}
  .fs-topbar-b{display:flex;align-items:center;gap:12px;padding:12px 16px;}

  .fs-counter{display:inline-flex;align-items:center;gap:7px;font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--fs-acc-d);background:var(--fs-acc-s);border-radius:999px;padding:6px 12px;}

  .fs-table{background:#fff;border:1px solid ${HEART.border};border-radius:18px;box-shadow:0 3px 12px rgba(27,14,6,.05);overflow:hidden;}
  .fs-tscroll{overflow-x:auto;}
  .fs-tgrid{min-width:940px;}
  .fs-thead,.fs-trow{display:grid;align-items:center;gap:12px;padding:0 16px;}
  .fs-thead{height:44px;background:${HEART.panel2};border-bottom:1px solid ${HEART.border};}
  .fs-th{font-size:10px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:${HEART.textFaint};white-space:nowrap;}
  .fs-trow{min-height:62px;border-bottom:1px solid ${HEART.divider};cursor:pointer;transition:background .1s;}
  .fs-trow:last-child{border-bottom:none;}
  .fs-trow:hover{background:${HEART.panel2};}
  .fs-trow.sel{background:var(--fs-acc-s);}
  .fs-titem{display:flex;align-items:center;gap:11px;min-width:0;}
  .fs-tthumb{width:42px;height:42px;border-radius:11px;overflow:hidden;flex-shrink:0;background:${HEART.panel2};border:1px solid ${HEART.border};display:flex;align-items:center;justify-content:center;}
  .fs-tthumb img{width:100%;height:100%;object-fit:cover;display:block;}
  .fs-tname{font-size:13px;font-weight:700;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .fs-tcell{font-size:12.5px;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
  .fs-tcell.muted{color:${HEART.textFaint};font-weight:500;}

  .fs-check{width:20px;height:20px;border-radius:6px;border:2px solid ${HEART.border};background:#fff;flex-shrink:0;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:border-color .15s,background .15s;color:#fff;}
  .fs-check.on{background:var(--fs-acc);border-color:var(--fs-acc);}

  .fs-bulk{position:fixed;left:50%;bottom:26px;transform:translateX(-50%);z-index:120;display:flex;align-items:center;gap:10px;background:${HEART.dark};color:#fff;padding:12px 18px;border-radius:16px;box-shadow:0 16px 40px rgba(27,14,6,.35);flex-wrap:wrap;justify-content:center;max-width:94vw;}
  .fs-bulk-btn{background:rgba(255,255,255,.12);border:none;color:#fff;font-size:12px;font-weight:700;padding:8px 14px;border-radius:10px;cursor:pointer;transition:background .15s;}
  .fs-bulk-btn:hover{background:rgba(255,255,255,.22);}
  .fs-bulk-btn.danger{background:#C8202E;}
  .fs-bulk-btn.danger:hover{background:#9E1521;}

  .fs-drop{border:1.5px dashed ${HEART.border};border-radius:16px;background:${HEART.panel2};display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px;padding:34px 16px;cursor:pointer;transition:border-color .15s,background .15s;text-align:center;}
  .fs-drop:hover{border-color:var(--fs-acc);background:var(--fs-acc-s);}
  .fs-drop.dragging{border-color:var(--fs-acc);background:var(--fs-acc-s);}

  .fs-thumb{position:relative;width:100%;aspect-ratio:1;border-radius:13px;overflow:hidden;border:1px solid ${HEART.border};background:${HEART.panel2};}
  .fs-thumb img{width:100%;height:100%;object-fit:cover;display:block;}
  .fs-thumb-del{position:absolute;top:6px;right:6px;width:23px;height:23px;border-radius:50%;background:rgba(27,14,6,.68);border:none;color:#fff;display:flex;align-items:center;justify-content:center;cursor:pointer;opacity:0;transition:opacity .12s;}
  .fs-thumb:hover .fs-thumb-del,.fs-thumb:focus-within .fs-thumb-del{opacity:1;}
  .fs-thumb-cover{position:absolute;left:6px;bottom:6px;background:linear-gradient(135deg,var(--fs-acc),var(--fs-acc-d));color:#fff;font-size:9px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;padding:4px 8px;border-radius:999px;}

  .fs-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:70px 20px;text-align:center;}
  .fs-divider{height:1px;background:${HEART.divider};margin:20px 0;border:none;}

  /* ── rich text editor ── */
  .fs-editor{border:1px solid ${HEART.border};border-radius:12px;overflow:hidden;background:#fff;transition:border-color .15s,box-shadow .15s;}
  .fs-editor.focused{border-color:var(--fs-acc);box-shadow:0 0 0 3px var(--fs-acc-s);}
  .fs-etoolbar{display:flex;align-items:center;gap:2px;flex-wrap:wrap;padding:7px 8px;border-bottom:1px solid ${HEART.divider};background:${HEART.panel2};}
  .fs-etool{display:flex;align-items:center;gap:3px;background:transparent;border:none;border-radius:7px;padding:6px 8px;cursor:pointer;color:${HEART.textMuted};transition:background .12s,color .12s;}
  .fs-etool:hover{background:var(--fs-acc-s);color:${HEART.dark};}
  .fs-etool.active{background:var(--fs-acc);color:#fff;}
  .fs-eselect{display:flex;align-items:center;gap:5px;background:transparent;border:none;border-radius:7px;padding:6px 9px;cursor:pointer;color:${HEART.dark};font-size:12.5px;font-weight:700;}
  .fs-eselect:hover{background:var(--fs-acc-s);}
  .fs-esep{width:1px;height:20px;background:${HEART.border};margin:0 5px;}
  .fs-ebody{width:100%;min-height:150px;max-height:340px;overflow-y:auto;padding:13px 14px;font-size:13.5px;line-height:1.65;outline:none;}
  .fs-ebody:empty::before{content:attr(data-placeholder);color:${HEART.textFaint};}
  .fs-ebody h1{font-family:'Cormorant Garamond',serif;font-size:24px;font-weight:700;margin:0 0 10px;}
  .fs-ebody h2{font-family:'Cormorant Garamond',serif;font-size:19px;font-weight:700;margin:0 0 8px;}
  .fs-ebody h3{font-family:'Cormorant Garamond',serif;font-size:16px;font-weight:700;margin:0 0 6px;}
  .fs-ebody p{margin:0 0 10px;}
  .fs-ebody ul,.fs-ebody ol{margin:0 0 10px;padding-left:20px;}
  .fs-ebody blockquote{margin:0 0 10px;padding:5px 0 5px 12px;border-left:3px solid var(--fs-acc);color:${HEART.textMuted};}
  .fs-ebody a{color:var(--fs-acc-d);text-decoration:underline;}
  .fs-ebody img,.fs-ebody iframe{max-width:100%;border-radius:10px;}

  .fs-modal-backdrop{position:fixed;inset:0;background:rgba(27,14,6,.4);z-index:250;animation:fsFadeIn .15s ease both;}
  .fs-modal{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:260;background:#fff;border-radius:20px;box-shadow:0 24px 60px rgba(27,14,6,.32);width:380px;max-width:92vw;padding:22px;animation:fsPop .18s ease both;}
  .fs-modal h3{font-family:'Cormorant Garamond',serif;font-size:20px;font-weight:700;margin:0 0 14px;}
  .fs-modal-actions{display:flex;gap:10px;margin-top:18px;}

  .fs-sheet{position:fixed;left:0;right:0;bottom:0;z-index:260;background:#fff;border-radius:22px 22px 0 0;box-shadow:0 -12px 40px rgba(27,14,6,.26);max-height:88vh;display:flex;flex-direction:column;animation:fsSheetUp .22s cubic-bezier(.32,.72,0,1) both;color:${HEART.dark};}
  .fs-sheet-handle{display:flex;justify-content:center;padding:10px 0 4px;}
  .fs-sheet-header{display:flex;align-items:center;justify-content:space-between;gap:10px;padding:2px 18px 12px;}
  .fs-sheet-body{overflow-y:auto;padding:6px 18px 28px;}

  .fs-picker-modal{position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);z-index:260;background:#fff;border-radius:22px;box-shadow:0 24px 60px rgba(27,14,6,.32);width:440px;max-width:92vw;max-height:86vh;display:flex;flex-direction:column;overflow:hidden;animation:fsPop .2s ease both;}

  .fs-trigger{width:100%;display:flex;align-items:center;justify-content:space-between;gap:10px;border:1px solid ${HEART.border};border-radius:12px;background:#fff;padding:11px 13px;font-size:13.5px;color:${HEART.dark};cursor:pointer;text-align:left;transition:border-color .15s;}
  .fs-trigger:hover{border-color:var(--fs-acc);}

  .fs-crumbs{display:flex;align-items:center;flex-wrap:wrap;gap:5px;padding:0 18px 9px;font-size:12px;}
  .fs-crumb{background:var(--fs-acc-s);border:1px solid ${HEART.border};color:var(--fs-acc-d);font-weight:700;padding:4px 10px;border-radius:999px;cursor:pointer;font-size:11px;}
  .fs-crumb:hover{background:var(--fs-acc);color:#fff;}
  .fs-crumb-sep{color:${HEART.textFaint};}
  .fs-catlist{overflow-y:auto;padding:4px 12px 14px;}
  .fs-catrow{display:flex;align-items:center;gap:11px;width:100%;border-radius:13px;padding:12px;transition:background .12s;}
  .fs-catrow:hover{background:${HEART.panel2};}
  .fs-catrow.sel{background:var(--fs-acc-s);}
  .fs-catic{width:34px;height:34px;border-radius:10px;background:var(--fs-acc-s);color:var(--fs-acc-d);display:flex;align-items:center;justify-content:center;font-size:15px;flex-shrink:0;}
  .fs-catname{flex:1;font-size:13.5px;font-weight:700;min-width:0;}
  .fs-catpick{font-size:10px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:var(--fs-acc);border:1px solid var(--fs-acc);background:#fff;padding:5px 10px;border-radius:999px;cursor:pointer;flex-shrink:0;}
  .fs-catpick:hover{background:var(--fs-acc);color:#fff;}

  .fs-attrfield{background:${HEART.panel2};border:1px solid ${HEART.divider};border-radius:14px;padding:13px 14px;transition:border-color .15s;}
  .fs-attrfield.wide{grid-column:1 / -1;}
  .fs-attrfield:focus-within{border-color:var(--fs-acc);}
  .fs-attrfield .fs-select,.fs-attrfield .fs-input{background:#fff;}
  .fs-attrreq{color:var(--fs-acc);margin-left:3px;}

  /* ── colour spheres ── */
  .fs-colorgrid{display:grid;grid-template-columns:repeat(auto-fill,minmax(70px,1fr));gap:10px;padding-top:4px;max-height:250px;overflow-y:auto;}
  .fs-colorcell{display:flex;flex-direction:column;align-items:center;gap:6px;background:none;border:none;padding:0;cursor:pointer;}
  .fs-sphere{width:38px;height:38px;border-radius:50%;position:relative;transition:transform .18s;}
  .fs-colorcell:hover .fs-sphere{transform:scale(1.1) translateY(-2px);}
  .fs-sphere.sel{outline:2.5px solid var(--fs-acc);outline-offset:3px;}
  .fs-spheretick{position:absolute;bottom:-3px;right:-3px;width:15px;height:15px;border-radius:50%;background:var(--fs-acc);display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.3);}
  .fs-colorname{font-size:10px;font-weight:600;color:${HEART.textMuted};text-align:center;line-height:1.2;max-width:66px;word-break:break-word;}
  .fs-colorcell.sel .fs-colorname{color:var(--fs-acc-d);font-weight:800;}

  /* ── per-colour variant cards ── */
  .fs-variant{border:1px solid ${HEART.border};border-radius:14px;background:#fff;padding:13px;display:flex;gap:12px;}
  .fs-variant + .fs-variant{margin-top:10px;}
  .fs-varshot{width:84px;height:98px;border-radius:11px;border:1.5px dashed ${HEART.border};background:${HEART.panel2};display:flex;flex-direction:column;align-items:center;justify-content:center;gap:5px;cursor:pointer;overflow:hidden;flex-shrink:0;position:relative;transition:border-color .15s;}
  .fs-varshot:hover{border-color:var(--fs-acc);}
  .fs-varshot img{width:100%;height:100%;object-fit:cover;display:block;}
  .fs-varhead{display:flex;align-items:center;gap:8px;margin-bottom:10px;}
  .fs-varswatch{width:14px;height:14px;border-radius:50%;border:1px solid rgba(0,0,0,.12);flex-shrink:0;}
  .fs-varname{font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;}
  .fs-inherit{display:inline-flex;align-items:center;gap:4px;font-size:10px;font-weight:800;letter-spacing:.04em;text-transform:uppercase;color:${HEART.textFaint};background:${HEART.panel2};border:1px solid ${HEART.border};border-radius:999px;padding:3px 8px;cursor:pointer;}
  .fs-inherit:hover{border-color:var(--fs-acc);color:var(--fs-acc-d);}

  /* ── line tables: sizes + extras ── */
  .fs-lines{border:1px solid ${HEART.border};border-radius:14px;overflow:hidden;background:${HEART.panel2};}
  .fs-linehead{display:grid;gap:8px;padding:9px 13px;background:var(--fs-acc-s);font-size:9.5px;font-weight:800;letter-spacing:.09em;color:var(--fs-acc-d);text-transform:uppercase;}
  .fs-linerow{display:grid;gap:8px;align-items:center;padding:10px 13px;border-top:1px solid ${HEART.divider};background:#fff;}
  .fs-linerow:first-of-type{border-top:none;}
  .fs-mini{border:1px solid ${HEART.border};border-radius:9px;background:#fff;padding:8px 10px;font-size:12.5px;color:${HEART.dark};outline:none;width:100%;min-width:0;transition:border-color .15s;}
  .fs-mini:focus{border-color:var(--fs-acc);}
  select.fs-mini{appearance:none;cursor:pointer;}
  .fs-linedel{width:28px;height:28px;border-radius:9px;border:none;background:#FBE7E9;color:#9E1521;display:flex;align-items:center;justify-content:center;cursor:pointer;flex-shrink:0;}
  .fs-linedel:hover{background:#f6cfd2;}
  .fs-lineempty{padding:22px 14px;text-align:center;font-size:12px;color:${HEART.textFaint};background:#fff;}

  /* ── ticket-style summary ── */
  .fs-ticket{background:#fff;border:1px solid ${HEART.border};border-radius:18px;padding:18px;box-shadow:0 3px 12px rgba(27,14,6,.05);}
  .fs-ticketrow{display:flex;align-items:baseline;justify-content:space-between;gap:10px;padding:8px 0;border-bottom:1px dashed ${HEART.divider};font-size:12px;}
  .fs-ticketrow:last-child{border-bottom:none;}
  .fs-ticketkey{font-weight:700;letter-spacing:.05em;text-transform:uppercase;color:${HEART.textFaint};font-size:10.5px;}

  .fs-toast{position:fixed;bottom:24px;right:24px;background:${HEART.dark};color:#fff;padding:12px 18px;border-radius:12px;font-size:13px;font-weight:700;z-index:300;box-shadow:0 12px 30px rgba(27,14,6,.3);}

  /* ── rate type: two selectable cards ── */
  .fs-rategrid{display:grid;grid-template-columns:1fr 1fr;gap:10px;}
  .fs-ratecard{display:flex;align-items:flex-start;gap:10px;text-align:left;border:2px solid ${HEART.border};border-radius:14px;background:#fff;padding:13px;cursor:pointer;transition:border-color .15s,background .15s,box-shadow .15s;}
  .fs-ratecard:hover{border-color:var(--fs-acc);}
  .fs-ratecard.on{border-color:var(--fs-acc);background:var(--fs-acc-s);box-shadow:0 4px 16px var(--fs-acc-glow);}
  .fs-ratecard-ic{width:30px;height:30px;border-radius:9px;background:var(--fs-acc-s);color:var(--fs-acc-d);display:flex;align-items:center;justify-content:center;flex-shrink:0;transition:background .15s,color .15s;}
  .fs-ratecard.on .fs-ratecard-ic{background:linear-gradient(135deg,var(--fs-acc),var(--fs-acc-d));color:#fff;}
  .fs-ratecard-t{font-size:13px;font-weight:800;margin:0 0 2px;}
  .fs-ratecard-b{font-size:11px;color:${HEART.textMuted};margin:0;line-height:1.45;}
  @media(max-width:520px){ .fs-rategrid{grid-template-columns:1fr;} }

  .fs-formhead{
    position:sticky;top:var(--fs-top,0px);z-index:40;
    display:flex;align-items:center;justify-content:space-between;gap:14px;flex-wrap:wrap;
    padding:12px 0 14px;margin-bottom:18px;
    border-bottom:1px solid ${HEART.divider};
    backdrop-filter:blur(8px);
  }
  .fs-sidebar{position:sticky;top:calc(var(--fs-top,0px) + 78px);}

  @media(max-width:900px){
    .fs-picker-modal{display:none !important;}
    .fs-formgrid{grid-template-columns:1fr !important;}
    .fs-mediagrid{grid-template-columns:repeat(3,1fr) !important;}
    .fs-toast{left:16px;right:16px;bottom:16px;text-align:center;}
    .fs-formhead{position:static;backdrop-filter:none;}
    .fs-sidebar{position:static;}
    .fs-variant{flex-direction:column;}
    .fs-varshot{width:100%;height:130px;}
  }
`;

/* ═══════════════════════════════════════════════════════════
   CATEGORY TREE
═══════════════════════════════════════════════════════════ */
const NODES = new Map<string, CategoryItem>(AllCategoriesFlat.map((c) => [c.id, c]));

const catPath = (id: string): CategoryItem[] => {
  const chain: CategoryItem[] = [];
  const seen = new Set<string>();
  let cur = NODES.get(id);
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    chain.unshift(cur);
    cur = cur.parentId ? NODES.get(cur.parentId) : undefined;
  }
  return chain;
};

/** The department a node ultimately belongs to. */
const deptOf = (id: string): string => catPath(id)[0]?.id ?? "";

const isUnder = (id: string, rootId: string) =>
  id !== rootId && catPath(id).some((c) => c.id === rootId);

const childrenOf = (parentId: string) => AllCategoriesFlat.filter((c) => c.parentId === parentId);
const hasChildren = (id: string) => AllCategoriesFlat.some((c) => c.parentId === id);

/** Everything below a set of departments, at any depth. */
const treeFor = (roots: string[]) =>
  AllCategoriesFlat.filter((c) => !roots.includes(c.id) && roots.includes(deptOf(c.id)));

/** Readable trail, department first so "Food › Dairy › Milk" reads straight. */
const pathLabel = (id: string) => catPath(id).map((c) => c.name).join(" › ");

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES
   CategoriesCollection ids are 2-digit per level ("09"), the
   attribute tree is 3-digit ("009"). Walk up the chain trying
   both widths until something answers.
═══════════════════════════════════════════════════════════ */
const attrKeysFor = (categoryId: string): string[] => {
  if (!categoryId) return [];
  return [...catPath(categoryId)]
    .reverse()
    .flatMap((c) => [`0${c.id}`, c.id])
    .filter(Boolean);
};

/** The key that actually resolved, plus the attributes it gave back. */
const resolveAttributes = (categoryId: string): { key: string; attrs: any[] } => {
  for (const key of attrKeysFor(categoryId)) {
    try {
      const found = attributesForCategory(key as any);
      if (found && found.length) return { key, attrs: found };
    } catch {
      /* unknown key — keep walking up */
    }
  }
  return { key: "", attrs: [] };
};

const isColorAttr = (a: any) => a?.inputType === "color" || /colou?r/i.test(String(a?.name ?? ""));

/**
 * Colour is injected into every category by the attribute collection.
 * A dish doesn't have a colour, so we only keep it when the category
 * itself declares one — or when the counter says colour is fair game.
 */
const declaresColor = (attrKey: string): boolean => {
  if (!attrKey) return false;
  try {
    const path = findNodePath(attrKey as any) || [];
    return path.some((n: any) => (n.attributes ?? []).some(isColorAttr));
  } catch {
    return false;
  }
};

/* ═══════════════════════════════════════════════════════════
   HELPERS
═══════════════════════════════════════════════════════════ */
type ViewMode = "list" | "form";

/** A size row. Name and unit are typed by the seller — nothing preset. */
export interface Portion {
  id: string;
  name: string;
   rate: number;// adjustment on top of the base rate
  unit: string;
  stock: number;
}
/** A paid add-on. */
export interface Extra {
  id: string;
  name: string;
  rate: number;
}
/** One row per selected colour. `rate: null` means "follow the base price". */
export interface Variant {
  colorId: string;
  name: string;
  hex?: string;
  imageUrl: string;
  previewUrl?: string;
  stock: number;
  rate: number | null;
}

const lineId = (p: string) => `${p}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

const emptyForm = () => ({
  name: "",
  description: "",
  category: "",
  categoryId: "",
  attributes: {} as Record<string, any>,
  variants: {} as Record<string, Variant>,

  rate: 0,
  rateType: DEFAULT_RATE_TYPE as RateType, // perProduct — one price for the listing
  compareAtPrice: 0,
  stock: 0,
  unit: "",
  available: true,

  netContent: 0,
  prepTimeMinutes: 0,
  calories: 0,

  allergens: [] as string[],

  portions: [] as Portion[],
  extras: [] as Extra[],

  shelfLifeDays: 0,
  harvestedOn: "",
  bestBefore: "",
  batchCode: "",

  photos: [] as string[],
  videoUrl: "",

  isPublished: false,
  isDraft: true,
});

type FormShape = ReturnType<typeof emptyForm>;

const money = (n?: number) =>
  `€${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ── the two ways an item is priced ──
   perProduct — one price, whatever quantity is ordered
   perItem    — priced by the unit, so each colour can carry its own */
const RATE_LABEL: Record<RateType, string> = {
  perProduct: "/ product",
  perItem: "/ item",
};

const RATE_COPY: Record<RateType, { title: string; blurb: string }> = {
  perProduct: {
    title: "Per product",
    blurb: "One price for the listing, however many they take.",
  },
  perItem: {
    title: "Per item",
    blurb: "Priced by the unit — each set its own.",
  },
};

const Money: React.FC<{ value?: number; large?: boolean }> = ({ value, large }) => (
  <span className={`fs-num${large ? " fs-num-lg" : ""}`}>{money(value)}</span>
);

/** Lit from above, shadowed below — reads as an object, not a swatch. */
const sphere3D = (hex: string) =>
  `radial-gradient(circle at 35% 32%, rgba(255,255,255,.55) 0%, rgba(255,255,255,.10) 28%, transparent 52%),` +
  `radial-gradient(circle at 68% 72%, rgba(0,0,0,.30) 0%, transparent 55%),` +
  `radial-gradient(circle at 50% 50%, ${hex} 0%, ${hex} 100%)`;

const LIGHT_HEX = new Set([
  "#FFFFFF", "#FFFFF0", "#FFFDD0", "#FAFAFA", "#E6E6FA", "#F5F5DC",
  "#87CEEB", "#98FF98", "#CDDC39", "#FFEB3B", "#FFC107", "#FFDB58", "#F8F8F0",
]);

function useIsMobile(breakpoint = 860) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < breakpoint : false
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

/* ═══════════════════════════════════════════════════════════
   NUMBER STEPPER
═══════════════════════════════════════════════════════════ */
const NumberStepper: React.FC<{
  value: number;
  onChange: (n: number) => void;
  min?: number;
  max?: number;
  step?: number;
  decimals?: boolean;
  suffix?: string;
  placeholder?: string;
  compact?: boolean;
}> = ({ value, onChange, min = 0, max = 9999999, step = 1, decimals = false, suffix, placeholder, compact }) => {
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  const bump = (d: number) => onChange(clamp(parseFloat(((value || 0) + d).toFixed(2))));
  const handle = (raw: string) => {
    let s = raw.replace(/[^0-9.]/g, "");
    if (!decimals) s = s.replace(/\./g, "");
    if (/^0\d/.test(s)) s = s.replace(/^0+/, "");
    if (s === "" || s === ".") return onChange(0);
    const n = decimals ? parseFloat(s) : parseInt(s, 10);
    onChange(clamp(isNaN(n) ? 0 : n));
  };
  const btn: React.CSSProperties = {
    width: compact ? 32 : 38, flexShrink: 0, fontSize: compact ? 15 : 18, fontWeight: 800,
    display: "flex", alignItems: "center", justifyContent: "center",
    padding: compact ? "6px 0" : undefined,
  };
  return (
    <div style={{ display: "flex", alignItems: "stretch", gap: 6 }}>
      <button type="button" className="fs-btn fs-btn-ghost" style={btn} onClick={() => bump(-step)} aria-label="Decrease">−</button>
      <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
        <input
          className={compact ? "fs-mini fs-num" : "fs-input fs-num"}
          type="text"
          inputMode={decimals ? "decimal" : "numeric"}
          style={{ textAlign: "center", paddingRight: suffix ? 40 : undefined }}
          value={value === 0 ? "" : String(value)}
          placeholder={placeholder ?? "0"}
          onChange={(e) => handle(e.target.value)}
        />
        {suffix && (
          <span style={{
            position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)",
            fontSize: 11, fontWeight: 700, color: HEART.textFaint, pointerEvents: "none",
          }}>
            {suffix}
          </span>
        )}
      </div>
      <button type="button" className="fs-btn fs-btn-ghost" style={btn} onClick={() => bump(step)} aria-label="Increase">+</button>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   RICH TEXT EDITOR
═══════════════════════════════════════════════════════════ */
const BLOCKS = [
  { label: "Paragraph", tag: "P" },
  { label: "Heading 1", tag: "H1" },
  { label: "Heading 2", tag: "H2" },
  { label: "Heading 3", tag: "H3" },
  { label: "Quote", tag: "BLOCKQUOTE" },
];

const RichTextEditor: React.FC<{
  value: string;
  onChange: (html: string) => void;
  resetKey: string | null;
  placeholder?: string;
}> = ({ value, onChange, resetKey, placeholder }) => {
  const bodyRef = useRef<HTMLDivElement>(null);
  const savedRangeRef = useRef<Range | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const [focused, setFocused] = useState(false);
  const [activeBlock, setActiveBlock] = useState("Paragraph");
  const [menuOpen, setMenuOpen] = useState(false);
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkText, setLinkText] = useState("");
  const [linkUrl, setLinkUrl] = useState("");
  const [active, setActive] = useState({ bold: false, italic: false, underline: false });

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (menuOpen && menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [menuOpen]);

  /* Load stored HTML only when switching items, never per keystroke. */
  useEffect(() => {
    if (bodyRef.current) bodyRef.current.innerHTML = value || "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  const sync = useCallback(() => {
    if (bodyRef.current) onChange(bodyRef.current.innerHTML);
  }, [onChange]);

  const refresh = useCallback(() => {
    try {
      setActive({
        bold: document.queryCommandState("bold"),
        italic: document.queryCommandState("italic"),
        underline: document.queryCommandState("underline"),
      });
      const block = String(document.queryCommandValue("formatBlock")).toLowerCase();
      const match = BLOCKS.find((b) => b.tag.toLowerCase() === block);
      setActiveBlock(match ? match.label : "Paragraph");
    } catch {
      /* queryCommandState throws outside a focused editable in some browsers */
    }
  }, []);

  useEffect(() => {
    const handler = () => {
      const sel = window.getSelection();
      if (sel && bodyRef.current && bodyRef.current.contains(sel.anchorNode)) refresh();
    };
    document.addEventListener("selectionchange", handler);
    return () => document.removeEventListener("selectionchange", handler);
  }, [refresh]);

  const exec = (cmd: string, arg?: string) => {
    bodyRef.current?.focus();
    document.execCommand(cmd, false, arg);
    sync();
    refresh();
  };

  const openLink = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount) savedRangeRef.current = sel.getRangeAt(0).cloneRange();
    setLinkText(sel ? sel.toString() : "");
    setLinkUrl("");
    setLinkOpen(true);
  };

  const submitLink = () => {
    if (!linkUrl.trim()) return setLinkOpen(false);
    const a = document.createElement("a");
    a.href = linkUrl.trim();
    a.target = "_blank";
    a.rel = "noopener";
    a.textContent = linkText.trim() || linkUrl.trim();

    bodyRef.current?.focus();
    const sel = window.getSelection();
    const range = savedRangeRef.current;
    if (sel && range) {
      sel.removeAllRanges();
      sel.addRange(range);
      range.deleteContents();
      range.insertNode(a);
      range.setStartAfter(a);
      range.collapse(true);
      sel.removeAllRanges();
      sel.addRange(range);
    } else {
      bodyRef.current?.appendChild(a);
    }
    sync();
    setLinkOpen(false);
  };

  const noBlur = (e: React.MouseEvent) => e.preventDefault();

  return (
    <div className={`fs-editor${focused ? " focused" : ""}`}>
      <div className="fs-etoolbar">
        <div ref={menuRef} style={{ position: "relative" }}>
          <button type="button" className="fs-eselect" onMouseDown={noBlur} onClick={() => setMenuOpen((v) => !v)}>
            {activeBlock} <ChevronDown size={13} />
          </button>
          {menuOpen && (
            <div className="fs-fade" style={{
              position: "absolute", top: "calc(100% + 6px)", left: 0, background: "#fff",
              border: `1px solid ${HEART.border}`, borderRadius: 12, boxShadow: "0 10px 24px rgba(27,14,6,.15)",
              width: 160, zIndex: 20, padding: 5,
            }}>
              {BLOCKS.map((b) => (
                <button
                  key={b.tag}
                  type="button"
                  onMouseDown={noBlur}
                  onClick={() => { exec("formatBlock", `<${b.tag}>`); setMenuOpen(false); }}
                  style={{
                    width: "100%", textAlign: "left", background: "transparent", border: "none",
                    borderRadius: 9, padding: "9px 10px", fontSize: 12.5, fontWeight: 700,
                    cursor: "pointer", color: HEART.dark,
                  }}
                >
                  {b.label}
                </button>
              ))}
            </div>
          )}
        </div>

        <span className="fs-esep" />
        <button type="button" className={`fs-etool${active.bold ? " active" : ""}`} onMouseDown={noBlur} onClick={() => exec("bold")} title="Bold"><Bold size={14} /></button>
        <button type="button" className={`fs-etool${active.italic ? " active" : ""}`} onMouseDown={noBlur} onClick={() => exec("italic")} title="Italic"><Italic size={14} /></button>
        <button type="button" className={`fs-etool${active.underline ? " active" : ""}`} onMouseDown={noBlur} onClick={() => exec("underline")} title="Underline"><Underline size={14} /></button>
        <span className="fs-esep" />
        <button type="button" className="fs-etool" onMouseDown={noBlur} onClick={() => exec("insertUnorderedList")} title="Bullet list"><ListIcon size={14} /></button>
        <button type="button" className="fs-etool" onMouseDown={noBlur} onClick={() => exec("insertOrderedList")} title="Numbered list"><ListOrdered size={14} /></button>
        <button type="button" className="fs-etool" onMouseDown={noBlur} onClick={() => exec("formatBlock", "<BLOCKQUOTE>")} title="Quote"><Quote size={14} /></button>
        <button type="button" className="fs-etool" onMouseDown={noBlur} onClick={openLink} title="Add link"><Link2 size={14} /></button>
        <button type="button" className="fs-etool" onMouseDown={noBlur} onClick={() => exec("justifyLeft")} title="Align left"><AlignLeft size={14} /></button>
        <span style={{ flex: 1 }} />
        <button type="button" className="fs-etool" onMouseDown={noBlur} onClick={() => exec("undo")} title="Undo"><Undo2 size={14} /></button>
        <button type="button" className="fs-etool" onMouseDown={noBlur} onClick={() => exec("redo")} title="Redo"><Redo2 size={14} /></button>
      </div>

      <div
        ref={bodyRef}
        className="fs-ebody"
        contentEditable
        suppressContentEditableWarning
        data-placeholder={placeholder}
        onInput={sync}
        onFocus={() => { setFocused(true); refresh(); }}
        onBlur={() => setFocused(false)}
        onKeyUp={refresh}
        onMouseUp={refresh}
      />

      {linkOpen && (
        <>
          <div className="fs-modal-backdrop" onClick={() => setLinkOpen(false)} />
          <div className="fs-modal">
            <h3>Add link</h3>
            <label className="fs-label">Link text</label>
            <input className="fs-input" placeholder="e.g. allergen sheet" value={linkText} onChange={(e) => setLinkText(e.target.value)} />
            <label className="fs-label" style={{ marginTop: 14 }}>URL</label>
            <input
              className="fs-input"
              placeholder="https://…"
              autoFocus
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && submitLink()}
            />
            <p className="fs-hint">Leave the text blank to show the URL itself.</p>
            <div className="fs-modal-actions">
              <button className="fs-btn fs-btn-ghost" style={{ flex: 1, padding: "11px 0" }} onClick={() => setLinkOpen(false)}>Cancel</button>
              <button className="fs-btn fs-btn-primary" style={{ flex: 1, padding: "11px 0" }} onClick={submitLink}>Add link</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   CATEGORY PICKER — department → category → subcategory →
   product type → product, for whichever roots this counter owns.
═══════════════════════════════════════════════════════════ */
const CategoryPicker: React.FC<{
  value: string;
  onChange: (id: string, label: string) => void;
  isMobile: boolean;
  roots: string[];
  title: string;
  /** Open inside this branch instead of at the top. Ignored if it has no children. */
  rootId?: string;
}> = ({ value, onChange, isMobile, roots, title, rootId }) => {
  const [open, setOpen] = useState(false);
  const [parent, setParent] = useState<string | null>(null); // null = top level
  const [query, setQuery] = useState("");

  const scopedRoot = rootId && hasChildren(rootId) ? rootId : null;
  const tree = useMemo(() => treeFor(roots), [roots.join("|")]);

  useEffect(() => setParent(null), [scopedRoot]);

  const selected = value ? NODES.get(value) : undefined;

  /** What the search covers, and what the current level lists. */
  const scope = useMemo(
    () => (scopedRoot ? tree.filter((c) => isUnder(c.id, scopedRoot)) : tree),
    [tree, scopedRoot]
  );

  const level = useMemo(() => {
    if (parent) return childrenOf(parent);
    if (scopedRoot) return childrenOf(scopedRoot);
    /* No scope: one root drops you straight into its categories,
       several roots show the counters first. */
    return roots.length === 1
      ? childrenOf(roots[0])
      : (roots.map((r) => NODES.get(r)).filter(Boolean) as CategoryItem[]);
  }, [parent, scopedRoot, roots.join("|")]);

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return level;
    return scope.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 60);
  }, [level, scope, query]);

  const topLabel = scopedRoot ? NODES.get(scopedRoot)?.name ?? "All" : "All";
  const crumbs = parent
    ? catPath(parent).filter((c) => (scopedRoot ? isUnder(c.id, scopedRoot) : !roots.includes(c.id) || roots.length > 1))
    : [];

  const pick = (item: CategoryItem) => {
    onChange(item.id, item.name);
    setOpen(false);
    setQuery("");
    setParent(null);
  };

  const goUp = () => {
    if (!parent) return;
    const chain = catPath(parent).filter((c) => (scopedRoot ? isUnder(c.id, scopedRoot) : true));
    const prev = chain.length >= 2 ? chain[chain.length - 2] : null;
    setParent(prev && !(roots.includes(prev.id) && roots.length === 1) ? prev.id : null);
  };

  const Body = (
    <>
      <div className="fs-sheet-header">
        <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
          {parent !== null && !query && (
            <button
              className="fs-btn fs-btn-ghost"
              style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 }}
              onClick={goUp}
              title="Back"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          <p style={{ fontSize: 15, fontWeight: 800, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
            {query ? "Search results" : parent ? NODES.get(parent)?.name : title}
          </p>
        </div>
        <button
          className="fs-btn fs-btn-ghost"
          style={{ width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 }}
          onClick={() => setOpen(false)}
        >
          <X size={15} />
        </button>
      </div>

      <div style={{ padding: "0 18px 10px" }}>
        <div className="fs-searchbar">
          <Search size={14} color={HEART.textFaint} />
          <input autoFocus placeholder="Search categories" value={query} onChange={(e) => setQuery(e.target.value)} />
          {query && (
            <button
              onClick={() => setQuery("")}
              style={{ background: HEART.border, border: "none", borderRadius: 999, width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: HEART.textMuted }}
            >
              <X size={11} />
            </button>
          )}
        </div>
      </div>

      {!query && (
        <div className="fs-crumbs">
          <span className="fs-crumb" onClick={() => setParent(null)}>{topLabel}</span>
          {crumbs.map((c) => (
            <React.Fragment key={c.id}>
              <span className="fs-crumb-sep">›</span>
              <span className="fs-crumb" onClick={() => setParent(c.id)}>{c.name}</span>
            </React.Fragment>
          ))}
        </div>
      )}

      <div className="fs-catlist" style={{ maxHeight: isMobile ? "52vh" : 380 }}>
        {rows.map((item) => {
          const drillable = hasChildren(item.id) && !query;
          return (
            <div key={item.id} className={`fs-catrow${value === item.id ? " sel" : ""}`}>
              <div
                style={{ display: "flex", alignItems: "center", gap: 11, flex: 1, minWidth: 0, cursor: "pointer" }}
                onClick={() => (drillable ? setParent(item.id) : pick(item))}
              >
                <div className="fs-catic">{item.icon}</div>
                <span className="fs-catname">
                  <span style={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {item.name}
                  </span>
                  {query && (
                    <span style={{ display: "block", fontSize: 10.5, fontWeight: 500, color: HEART.textFaint }}>
                      {pathLabel(item.id).split(" › ").slice(0, -1).join(" › ")}
                    </span>
                  )}
                </span>
              </div>
              <button className="fs-catpick" onClick={(e) => { e.stopPropagation(); pick(item); }}>Choose</button>
              {drillable && (
                <ChevronDown
                  size={16}
                  style={{ transform: "rotate(-90deg)", color: HEART.textFaint, cursor: "pointer", flexShrink: 0 }}
                  onClick={() => setParent(item.id)}
                />
              )}
            </div>
          );
        })}
        {rows.length === 0 && (
          <p className="fs-hint" style={{ textAlign: "center", padding: "24px 0" }}>
            Nothing matches “{query}”. Try a shorter word.
          </p>
        )}
      </div>
    </>
  );

  return (
    <>
      <button type="button" className="fs-trigger" onClick={() => setOpen(true)}>
        {selected ? (
          <span style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
            <span style={{ display: "flex", color: "var(--fs-acc-d)" }}>{selected.icon}</span>
            <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {pathLabel(selected.id)}
            </span>
          </span>
        ) : (
          <span style={{ color: HEART.textFaint }}>Pick a category</span>
        )}
        <ChevronDown size={14} color={HEART.textFaint} />
      </button>

      {open && (
        <>
          <div className="fs-modal-backdrop" onClick={() => setOpen(false)} />
          {isMobile ? (
            <div className="fs-sheet">
              <div className="fs-sheet-handle"><GripHorizontal size={18} color={HEART.textFaint} /></div>
              {Body}
            </div>
          ) : (
            <div className="fs-picker-modal">{Body}</div>
          )}
        </>
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════
   COLOUR SPHERES
═══════════════════════════════════════════════════════════ */
const ColorSpheres: React.FC<{
  options: any[];
  selected: string[];
  onToggle: (id: string) => void;
}> = ({ options, selected, onToggle }) => (
  <div className="fs-colorgrid">
    {options.map((opt) => {
      const id = String(opt.id);
      const on = selected.includes(id);
      const hex = opt.hex as string | undefined;
      const light = hex && LIGHT_HEX.has(hex.toUpperCase());
      return (
        <button
          key={id}
          type="button"
          className={`fs-colorcell${on ? " sel" : ""}`}
          onClick={() => onToggle(id)}
          title={opt.name}
        >
          <span style={{ position: "relative", width: 38, height: 38 }}>
            <span
              className={`fs-sphere${on ? " sel" : ""}`}
              style={{
                display: "block",
                background: hex
                  ? sphere3D(hex)
                  : "conic-gradient(#e53935 0deg 51deg,#FF9800 51deg 102deg,#FFEB3B 102deg 153deg,#228B22 153deg 204deg,#4169E1 204deg 255deg,#9C27B0 255deg 306deg,#e53935 306deg 360deg)",
                boxShadow: light
                  ? "inset -5px -4px 10px rgba(0,0,0,.12),inset 4px 4px 8px rgba(255,255,255,.6),0 1px 0 rgba(0,0,0,.15),0 4px 12px rgba(0,0,0,.12)"
                  : "inset -6px -5px 12px rgba(0,0,0,.30),inset 5px 5px 10px rgba(255,255,255,.18),0 5px 18px rgba(0,0,0,.20)",
              }}
            />
            {on && (
              <span className="fs-spheretick">
                <Check size={9} color="#fff" strokeWidth={3} />
              </span>
            )}
          </span>
          <span className="fs-colorname">{opt.name}</span>
        </button>
      );
    })}
  </div>
);

/* ═══════════════════════════════════════════════════════════
   VARIANT CARD — one per selected colour. Price starts on the
   base price and stays there until someone changes it.
═══════════════════════════════════════════════════════════ */
const VariantCard: React.FC<{
  variant: Variant;
  baseRate: number;
  rateType: RateType;
  uploading: boolean;
  onUpdate: (colorId: string, patch: Partial<Variant>) => void;
  onPickImage: (colorId: string, file: File) => void;
  onRemove: (colorId: string) => void;
}> = ({ variant, baseRate, rateType, uploading, onUpdate, onPickImage, onRemove }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const shot = variant.previewUrl || variant.imageUrl;
  const inherits = variant.rate === null || variant.rate === undefined;
  const shownRate = inherits ? baseRate : (variant.rate as number);

  return (
    <div className="fs-variant">
      <div className="fs-varshot" onClick={() => fileRef.current?.click()}>
        {uploading ? (
          <span className="fs-spin" style={{ width: 18, height: 18, border: `2px solid ${HEART.border}`, borderTopColor: "var(--fs-acc)", borderRadius: "50%" }} />
        ) : shot ? (
          <img src={shot} alt={variant.name} />
        ) : (
          <>
            <Camera size={16} color="var(--fs-acc-d)" />
            <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: ".08em", color: HEART.textMuted }}>PHOTO</span>
          </>
        )}
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) onPickImage(variant.colorId, f);
            e.target.value = "";
          }}
        />
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="fs-varhead">
          {variant.hex && <span className="fs-varswatch" style={{ background: variant.hex }} />}
          <span className="fs-varname">{variant.name}</span>
          <button
            className="fs-btn"
            style={{ marginLeft: "auto", background: "none", color: HEART.textFaint, padding: 4, display: "flex" }}
            onClick={() => onRemove(variant.colorId)}
            aria-label={`Remove ${variant.name}`}
          >
            <X size={14} />
          </button>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: rateType === "perItem" ? "1fr 1fr" : "1fr", gap: 10 }}>
          <div>
            <label className="fs-label" style={{ marginBottom: 5 }}>Quantity</label>
            <NumberStepper compact value={variant.stock} onChange={(n) => onUpdate(variant.colorId, { stock: n })} step={1} />
          </div>

          {rateType === "perItem" ? (
            <div>
              <label className="fs-label" style={{ marginBottom: 5, display: "flex", alignItems: "center", gap: 6 }}>
                Price <span style={{ color: HEART.textFaint }}>/ item</span>
                {inherits ? (
                  <span style={{ fontSize: 9, fontWeight: 800, color: HEART.textFaint }}>· base</span>
                ) : (
                  <button className="fs-inherit" onClick={() => onUpdate(variant.colorId, { rate: null })} title="Follow the base price again">
                    <RotateCcw size={9} /> reset
                  </button>
                )}
              </label>
              <NumberStepper
                compact
                decimals
                step={0.5}
                suffix="€"
                value={shownRate}
                onChange={(n) => onUpdate(variant.colorId, { rate: n })}
              />
            </div>
          ) : (
            <p className="fs-hint" style={{ marginTop: 2 }}>
              Priced per product — this colour sells at the listing price of {money(baseRate)}.
              Switch to <strong>Per item</strong> to price it on its own.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   ATTRIBUTES — every field, option and unit below comes from
   the attribute collection. Nothing is written in this file.
═══════════════════════════════════════════════════════════ */
const AttributesRenderer: React.FC<{
  attrs: any[];
  values: Record<string, any>;
  onChange: (attrId: string, value: any) => void;
  isMobile: boolean;
  title: string;
  colorAttrId: string | null;
  onToggleColor: (colorId: string) => void;
}> = ({ attrs, values, onChange, isMobile, title, colorAttrId, onToggleColor }) => {
  if (attrs.length === 0) return null;

  return (
    <div className="fs-card fs-fade">
      <div className="fs-cardhead">
        <div className="fs-cardic"><Tag size={15} /></div>
        <p className="fs-cardtitle" style={{ margin: 0 }}>{title}</p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
        {attrs.map((attr: any) => {
          const key = String(attr.id);
          const isColor = key === colorAttrId;
          const label = (
            <label className="fs-label">
              {attr.name}
              {attr.required && <span className="fs-attrreq">*</span>}
            </label>
          );

          if (isColor) {
            const picked: string[] = Array.isArray(values[key]) ? values[key] : [];
            return (
              <div key={key} className="fs-attrfield wide">
                {label}
                <ColorSpheres options={attr.options ?? []} selected={picked} onToggle={onToggleColor} />
                <p className="fs-hint">Each colour you pick gets its own photo, quantity and price below.</p>
              </div>
            );
          }

          return (
            <div key={key} className="fs-attrfield">
              {label}

              {(attr.inputType === "select" || !attr.inputType) && (
                <select className="fs-select" value={values[key] ?? ""} onChange={(e) => onChange(key, e.target.value)}>
                  <option value="">Choose {String(attr.name).toLowerCase()}</option>
                  {attr.options?.map((opt: any) => (
                    <option key={opt.id} value={opt.id}>{opt.name || opt.standard}</option>
                  ))}
                </select>
              )}

              {attr.inputType === "multiselect" && (
                <div className="fs-chips">
                  {attr.options?.map((opt: any) => {
                    const list: any[] = Array.isArray(values[key]) ? values[key] : [];
                    const on = list.includes(opt.id);
                    return (
                      <button
                        key={opt.id}
                        type="button"
                        className={`fs-chip${on ? " on" : ""}`}
                        onClick={() => onChange(key, on ? list.filter((v) => v !== opt.id) : [...list, opt.id])}
                      >
                        {opt.name || opt.standard}
                      </button>
                    );
                  })}
                </div>
              )}

              {attr.inputType === "text" && (
                <input
                  className="fs-input"
                  value={values[key] ?? ""}
                  onChange={(e) => onChange(key, e.target.value)}
                  placeholder={`Enter ${String(attr.name).toLowerCase()}`}
                />
              )}

              {attr.inputType === "number" && (
                <NumberStepper
                  value={Number(values[key]) || 0}
                  onChange={(n) => onChange(key, n)}
                  step={1}
                  decimals
                  suffix={attr.unit}
                />
              )}

              {attr.required && <p className="fs-hint">Needed before this can go live.</p>}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   PRICE & STOCK
═══════════════════════════════════════════════════════════ */
const PriceStockFields: React.FC<{
  form: FormShape;
  cfg: VerticalConfig;
  hasVariants: boolean;
  set: <K extends keyof FormShape>(key: K, value: FormShape[K]) => void;
}> = ({ form, cfg, hasVariants, set }) => (
  <>
    <p className="fs-cardtitle" style={{ marginBottom: 12 }}>How you charge</p>

    <div className="fs-rategrid" style={{ marginBottom: 14 }}>
      {(["perProduct", "perItem"] as RateType[]).map((rt) => (
        <button
          key={rt}
          type="button"
          className={`fs-ratecard${form.rateType === rt ? " on" : ""}`}
          onClick={() => set("rateType", rt)}
        >
          <span className="fs-ratecard-ic">
            {rt === "perProduct" ? <ShoppingBasket size={15} /> : <Ruler size={15} />}
          </span>
          <span style={{ minWidth: 0 }}>
            <p className="fs-ratecard-t">{RATE_COPY[rt].title}</p>
            <p className="fs-ratecard-b">{RATE_COPY[rt].blurb}</p>
          </span>
        </button>
      ))}
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
      <div>
        <label className="fs-label">Base price {RATE_LABEL[form.rateType]}</label>
        <NumberStepper value={form.rate} onChange={(n) => set("rate", n)} step={0.5} decimals suffix="€" />
        {hasVariants && (
          <p className="fs-hint">
            {form.rateType === "perItem"
              ? "Every colour starts here until you give it its own price."
              : "One price covers the listing — colours share it."}
          </p>
        )}
      </div>
      <div>
        <label className="fs-label">Was (crossed out)</label>
        <NumberStepper value={form.compareAtPrice} onChange={(n) => set("compareAtPrice", n)} step={0.5} decimals suffix="€" />
      </div>
    </div>

    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
      <div>
        <label className="fs-label">{cfg.stockLabel}</label>
        <NumberStepper value={form.stock} onChange={(n) => set("stock", n)} step={1} />
        {(form.portions.length > 0 || hasVariants) && (
          <p className="fs-hint">
            {hasVariants
              ? "Colours carry their own quantity — this covers anything sold plain."
              : "Sizes carry their own stock — this covers anything sold without a size."}
          </p>
        )}
      </div>
      <div>
        <label className="fs-label">Serving size</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 110px", gap: 8 }}>
          <NumberStepper value={form.netContent} onChange={(n) => set("netContent", n)} step={10} decimals />
          <input
            className="fs-input"
            placeholder="unit"
            value={form.unit}
            onChange={(e) => set("unit", e.target.value)}
            style={{ textAlign: "center" }}
          />
        </div>
        <p className="fs-hint">Type the unit you use — g, ml, pcs, bunch.</p>
      </div>
    </div>

    <div className="fs-togglerow" style={{ borderTop: `1px solid ${HEART.divider}` }}>
      <span className="fs-togglelabel">Available right now</span>
      <button className={`fs-toggle${form.available ? " on" : ""}`} onClick={() => set("available", !form.available)} aria-label="Available right now">
        <span />
      </button>
    </div>
    <p className="fs-hint" style={{ marginTop: 0 }}>
      Turn this off to grey the {cfg.item} out without unpublishing it.
    </p>
  </>
);

/* ═══════════════════════════════════════════════════════════
   SIZE ROWS
═══════════════════════════════════════════════════════════ */
const PortionsSection: React.FC<{
  portions: Portion[];
  isMobile: boolean;
  cfg: VerticalConfig;
  onUpdate: (id: string, patch: Partial<Portion>) => void;
  onRemove: (id: string) => void;
}> = ({ portions, isMobile, cfg, onUpdate, onRemove }) => {
  if (portions.length === 0) {
    return (
      <div className="fs-lines">
        <div className="fs-lineempty">No rows yet — the base price above is what buyers pay.</div>
      </div>
    );
  }
  const cols = isMobile ? "1fr 74px 34px" : "1.4fr 116px 96px 90px 34px";
  return (
    <div className="fs-lines">
      {!isMobile && (
        <div className="fs-linehead" style={{ gridTemplateColumns: cols }}>
          <span>Name</span><span>Price on top</span><span>Unit</span><span>Stock</span><span />
        </div>
      )}
      {portions.map((p) =>
        isMobile ? (
          <div key={p.id} className="fs-linerow" style={{ gridTemplateColumns: cols }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 0 }}>
              <input className="fs-mini" value={p.name} placeholder={cfg.sizeNamePlaceholder} onChange={(e) => onUpdate(p.id, { name: e.target.value })} />
              <div style={{ display: "flex", gap: 6 }}>
                <input className="fs-mini fs-num" type="number" value={p.rate || ""} placeholder="+0.00" onChange={(e) => onUpdate(p.id, { rate: +e.target.value })} />
                <input className="fs-mini" value={p.unit} placeholder="unit" onChange={(e) => onUpdate(p.id, { unit: e.target.value })} />
              </div>
            </div>
            <input className="fs-mini fs-num" type="number" value={p.stock || ""} placeholder="stock" onChange={(e) => onUpdate(p.id, { stock: +e.target.value })} />
            <button className="fs-linedel" onClick={() => onRemove(p.id)} aria-label="Remove row"><X size={13} /></button>
          </div>
        ) : (
          <div key={p.id} className="fs-linerow" style={{ gridTemplateColumns: cols }}>
            <input className="fs-mini" value={p.name} placeholder={cfg.sizeNamePlaceholder} onChange={(e) => onUpdate(p.id, { name: e.target.value })} />
            <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
              <span style={{ fontSize: 12, color: "var(--fs-acc-d)", fontWeight: 800, flexShrink: 0 }}>+€</span>
              <input className="fs-mini fs-num" type="number" value={p.rate || ""} placeholder="0.00" onChange={(e) => onUpdate(p.id, { rate: +e.target.value })} />
            </div>
            <input className="fs-mini" value={p.unit} placeholder="330ml" onChange={(e) => onUpdate(p.id, { unit: e.target.value })} />
            <input className="fs-mini fs-num" type="number" value={p.stock || ""} placeholder="0" onChange={(e) => onUpdate(p.id, { stock: +e.target.value })} />
            <button className="fs-linedel" onClick={() => onRemove(p.id)} aria-label="Remove row"><X size={13} /></button>
          </div>
        )
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   EXTRAS
═══════════════════════════════════════════════════════════ */
const ExtrasSection: React.FC<{
  extras: Extra[];
  isMobile: boolean;
  onUpdate: (id: string, patch: Partial<Extra>) => void;
  onRemove: (id: string) => void;
}> = ({ extras, isMobile, onUpdate, onRemove }) => {
  if (extras.length === 0) {
    return (
      <div className="fs-lines">
        <div className="fs-lineempty">No extras yet — add sauces, toppings or sides above.</div>
      </div>
    );
  }
  const cols = isMobile ? "1fr 84px 34px" : "1fr 130px 34px";
  return (
    <div className="fs-lines">
      {!isMobile && (
        <div className="fs-linehead" style={{ gridTemplateColumns: cols }}>
          <span>Extra</span><span>Price</span><span />
        </div>
      )}
      {extras.map((e) => (
        <div key={e.id} className="fs-linerow" style={{ gridTemplateColumns: cols }}>
          <input className="fs-mini" value={e.name} placeholder="Extra cheese" onChange={(ev) => onUpdate(e.id, { name: ev.target.value })} />
          <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
            <span style={{ fontSize: 12, color: "var(--fs-acc-d)", fontWeight: 800, flexShrink: 0 }}>+€</span>
            <input className="fs-mini fs-num" type="number" value={e.rate || ""} placeholder="0.00" onChange={(ev) => onUpdate(e.id, { rate: +ev.target.value })} />
          </div>
          <button className="fs-linedel" onClick={() => onRemove(e.id)} aria-label="Remove extra"><X size={13} /></button>
        </div>
      ))}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN
═══════════════════════════════════════════════════════════ */
const FreshStore: React.FC<{
  brandId: string;
  listingId?: string;
  /**
   * Which counter this screen is. Leave it out and it follows the
   * department of whatever category the item sits in.
   */
  vertical?: Vertical;
  /**
   * Optional node this screen is scoped to. When it points at a real
   * category it seeds new items, opens the picker inside that branch,
   * and narrows the list to that branch.
   */
  subcategoryId?: string | number | null;
  /** Route base for this screen. Defaults to /store/:brandId/fresh */
  basePath?: string;
  /** Height of the dashboard header above, so sticky bits clear it. */
  headerOffset?: number;
}> = ({ brandId, listingId, vertical, subcategoryId, basePath, headerOffset = 0 }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const isMobile = useIsMobile();

  const base = basePath || `/store/${brandId}/fresh`;

  const { brandFoodItems, brandFoodLoading } = useSelector((state: RootState) => state.food);

  const view: ViewMode = listingId ? "form" : "list";

  const [editingId, setEditingId] = useState<string | null>(
    listingId && listingId !== "new" ? listingId : null
  );

  /* ── which counter are we? ───────────────────────────────
     An explicit prop wins. Otherwise the chosen category's
     department decides, so one screen serves all three.       */
  const [form, setForm] = useState<FormShape>(() => emptyForm());

  const activeVertical: Vertical = useMemo(() => {
    if (vertical) return vertical;
    const b = form.categoryId ? branchOf(form.categoryId) : "";
    return BRANCH_VERTICAL[b] ?? "fastfood";
  }, [vertical, form.categoryId]);

  const cfg = VERTICALS[activeVertical];

  /* Trust the scope prop only if it's a real node inside this vertical's
     own branch; otherwise fall back to that branch itself so fastfood,
     grocery and farm stay separated even though they share dept "08". */
  const scopeId = useMemo(() => {
    const raw = subcategoryId == null ? "" : String(subcategoryId);
    const fallback = VERTICAL_BRANCH[cfg.key];
    if (raw && NODES.has(raw) && isUnder(raw, fallback)) return raw;
    if (raw === fallback) return fallback;
    return fallback;
  }, [subcategoryId, cfg.key]);

  const blankForm = useCallback((): FormShape => {
    const f = emptyForm();
    const node = scopeId ? NODES.get(scopeId) : undefined;
    if (node) {
      f.categoryId = node.id;
      f.category = node.name;
    }
    return f;
  }, [scopeId]);

  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [variantUploading, setVariantUploading] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "live" | "draft">("all");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [toast, setToast] = useState<string | null>(null);
  const [priceSheetOpen, setPriceSheetOpen] = useState(false);
  const [allergenDraft, setAllergenDraft] = useState("");

  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const rootStyle = {
    ["--fs-top" as any]: `${headerOffset}px`,
    ["--fs-acc" as any]: cfg.accent,
    ["--fs-acc-d" as any]: cfg.accentDark,
    ["--fs-acc-s" as any]: cfg.accentSoft,
    ["--fs-acc-glow" as any]: `${cfg.accent}47`,
  } as React.CSSProperties;

  const set = <K extends keyof FormShape>(key: K, value: FormShape[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const setDescription = useCallback((html: string) => set("description", html), []);

  /* ── attributes for the chosen category ── */
  const { key: attrKey, attrs: rawAttrs } = useMemo(
    () => resolveAttributes(form.categoryId),
    [form.categoryId]
  );

  const attrs = useMemo(() => {
    if (!form.categoryId) return [];
    const colourIsReal = cfg.allowUniversalColor || declaresColor(attrKey);
    const merged = [...rawAttrs];
    /* Guarantee the counter's own staples even where the tree is thin. */
    for (const extra of cfg.ensure) {
      if (extra && !merged.some((a) => String(a.id) === String(extra.id))) merged.push(extra);
    }
    return merged.filter((a) => (isColorAttr(a) ? colourIsReal : true));
  }, [rawAttrs, attrKey, cfg, form.categoryId]);

  const colorAttrId = useMemo(() => {
    const hit = attrs.find(isColorAttr);
    return hit ? String(hit.id) : null;
  }, [attrs]);

  const colorOptions = useMemo(() => {
    const hit = attrs.find(isColorAttr);
    return (hit?.options ?? []) as any[];
  }, [attrs]);

  const selectedColors: string[] = useMemo(() => {
    if (!colorAttrId) return [];
    const v = form.attributes[colorAttrId];
    return Array.isArray(v) ? v.map(String) : [];
  }, [form.attributes, colorAttrId]);

  const variantList = useMemo(
    () => selectedColors.map((id) => form.variants[id]).filter(Boolean) as Variant[],
    [selectedColors, form.variants]
  );
  const hasVariants = variantList.length > 0;

  /* ── colour → variant sync ── */
  const toggleColor = (colorId: string) => {
    if (!colorAttrId) return;
    setForm((f) => {
      const current: string[] = Array.isArray(f.attributes[colorAttrId])
        ? f.attributes[colorAttrId].map(String)
        : [];
      const on = current.includes(colorId);
      const nextIds = on ? current.filter((c) => c !== colorId) : [...current, colorId];
      const variants = { ...f.variants };
      if (on) {
        delete variants[colorId];
      } else {
        const opt = colorOptions.find((o) => String(o.id) === colorId);
        variants[colorId] = {
          colorId,
          name: opt?.name ?? "Colour",
          hex: opt?.hex,
          imageUrl: "",
          stock: 0,
          rate: null, // follows the base price until changed
        };
      }
      return { ...f, attributes: { ...f.attributes, [colorAttrId]: nextIds }, variants };
    });
  };

  const updateVariant = (colorId: string, patch: Partial<Variant>) =>
    setForm((f) => {
      const existing = f.variants[colorId];
      if (!existing) return f;
      return { ...f, variants: { ...f.variants, [colorId]: { ...existing, ...patch } } };
    });

  /* ── hydrate the form from the feed ── */
  const hydrate = (item: FoodItem): FormShape => {
    const raw: any = item;

    const portions: Portion[] = Array.isArray(raw.portions)
      ? raw.portions.map((p: any) => ({
          id: String(p.id ?? lineId("pt")),
          name: p.name ?? "",
 rate: Number(p.rate ?? p.price) || 0,
          unit: p.unit ?? "",
          stock: Number(p.stock) || 0,
        }))
      : Object.entries(raw.portions || {}).map(([k, v]: [string, any]) => ({
          id: k,
          name: v?.name ?? k,
        rate: Number(v?.rate ?? v?.price) || 0,
          unit: v?.unit ?? "",
          stock: Number(v?.stock) || 0,
        }));

    const variants: Record<string, Variant> = {};
    Object.entries(raw.variants || {}).forEach(([k, v]: [string, any]) => {
      variants[String(k)] = {
        colorId: String(k),
        name: v?.name ?? "Colour",
        hex: v?.hex,
        imageUrl: v?.imageUrl ?? "",
        stock: Number(v?.stock) || 0,
        rate: v?.rate === null || v?.rate === undefined ? null : Number(v.rate),
      };
    });

    return {
      name: raw.name || "",
      description: raw.description || "",
      category: raw.category || "",
      categoryId: String(raw.categoryId ?? ""),
      attributes: raw.attributes || {},
      variants,

      rate: raw.rate || 0,
      rateType: (raw.rateType as RateType) || DEFAULT_RATE_TYPE,
      compareAtPrice: raw.compareAtPrice || 0,
      stock: raw.stock || 0,
      unit: raw.unit || "",
      available: raw.available ?? true,

      netContent: raw.netContent || 0,
      prepTimeMinutes: raw.prepTimeMinutes || 0,
      calories: raw.calories || 0,

      allergens: Array.isArray(raw.allergens) ? raw.allergens : [],

      portions,
      extras: Array.isArray(raw.extras)
        ? raw.extras.map((e: any) => ({
            id: String(e.id ?? lineId("ex")),
            name: e.name ?? "",
            rate: Number(e.rate ?? e.price) || 0,
          }))
        : [],

      shelfLifeDays: raw.shelfLifeDays || 0,
      harvestedOn: raw.harvestedOn || "",
      bestBefore: raw.bestBefore || "",
      batchCode: raw.batchCode || "",

      photos: raw.photos || [],
      videoUrl: raw.videoUrl || "",

      isPublished: raw.isPublished ?? false,
      isDraft: raw.isDraft ?? !raw.isPublished,
    };
  };

  useEffect(() => {
    if (!listingId || listingId === "new") {
      setEditingId(null);
      setForm(blankForm());
      return;
    }
    if (listingId === editingId) return; // already filled by openEdit
    const existing = brandFoodItems.find((i) => i.listingId === listingId);
    if (existing) {
      setEditingId(existing.listingId);
      setForm(hydrate(existing));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId, brandFoodItems]);

  /* ── brand-scoped listener ── */
  useEffect(() => {
    if (!brandId) return;
    dispatch(listenToBrandFoodFeed(brandId) as any);
    return () => {
      dispatch(stopBrandFoodFeed() as any);
    };
  }, [brandId, dispatch]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2600);
    return () => clearTimeout(t);
  }, [toast]);

  /* ── filtering ── */
  const scopedItems = useMemo(() => {
    return brandFoodItems.filter((i) => {
      const id = String((i as any).categoryId ?? "");
      if (scopeId) return id === scopeId || isUnder(id, scopeId);
      if (!id) return true; // uncategorised items stay visible
      return vertical ? cfg.roots.includes(deptOf(id)) : true;
    });
  }, [brandFoodItems, scopeId, vertical, cfg.roots.join("|")]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return scopedItems.filter((i) => {
      const matchesQuery =
        !q ||
        (i.name || "").toLowerCase().includes(q) ||
        (i.category || "").toLowerCase().includes(q);
      const matchesStatus =
        statusFilter === "all" ||
        (statusFilter === "live" && i.isPublished) ||
        (statusFilter === "draft" && !i.isPublished);
      return matchesQuery && matchesStatus;
    });
  }, [scopedItems, search, statusFilter]);

  const counts = useMemo(
    () => ({
      all: scopedItems.length,
      live: scopedItems.filter((i) => i.isPublished).length,
      draft: scopedItems.filter((i) => !i.isPublished).length,
    }),
    [scopedItems]
  );

  /* ── selection & bulk ── */
  const toggleSelect = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () =>
    setSelectedIds((prev) =>
      prev.size === filtered.length ? new Set() : new Set(filtered.map((i) => i.listingId))
    );

  const bulkPublish = async (published: boolean) => {
    await Promise.all(
      Array.from(selectedIds).map((id) =>
        dispatch(
          upsertFoodItem({ listingId: id, brandId, data: { isPublished: published, isDraft: !published } })
        ).unwrap().catch(() => {})
      )
    );
    setToast(published ? "Published" : "Moved to drafts");
    setSelectedIds(new Set());
  };

  const bulkAvailability = async (available: boolean) => {
    await Promise.all(
      Array.from(selectedIds).map((id) =>
        dispatch(upsertFoodItem({ listingId: id, brandId, data: { available } })).unwrap().catch(() => {})
      )
    );
    setToast(available ? `Back ${cfg.liveTab.toLowerCase()}` : "Marked sold out");
    setSelectedIds(new Set());
  };

  const bulkDelete = async () => {
    await Promise.all(Array.from(selectedIds).map((id) => dispatch(deleteFoodItem(id))));
    setToast("Deleted");
    setSelectedIds(new Set());
  };

  /* ── navigation ── */
  const openNew = () => navigate(`${base}/new`);
  const backToList = () => navigate(base);

  const openEdit = (item: FoodItem) => {
    setEditingId(item.listingId);
    setForm(hydrate(item));
    navigate(`${base}/${item.listingId}`);
  };

 /* ── rows ── */
  const addPortion = () =>
    set("portions", [...form.portions, { id: lineId("pt"), name: "", rate: 0, unit: "", stock: 0 }]);
  const updatePortion = (id: string, patch: Partial<Portion>) =>
    set("portions", form.portions.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const removePortion = (id: string) => set("portions", form.portions.filter((p) => p.id !== id));

  const addExtra = () => set("extras", [...form.extras, { id: lineId("ex"), name: "", rate: 0 }]);
  const updateExtra = (id: string, patch: Partial<Extra>) =>
    set("extras", form.extras.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  const removeExtra = (id: string) => set("extras", form.extras.filter((e) => e.id !== id));
  const addAllergen = () => {
    const v = allergenDraft.trim();
    if (!v) return;
    if (form.allergens.some((a) => a.toLowerCase() === v.toLowerCase())) {
      setAllergenDraft("");
      return;
    }
    set("allergens", [...form.allergens, v]);
    setAllergenDraft("");
  };

  /* ── save ── */
  const handleSave = async () => {
    if (!form.name.trim()) {
      setToast(`Give the ${cfg.item} a name first`);
      return;
    }
    setSaving(true);
    try {
      const pricedPerItem = form.rateType === "perItem" && variantList.length > 0;

      /* Build variants map — only keep variants whose colour is still selected */
const variantsForSave: Record<string, any> = Object.fromEntries(
  variantList.map((v) => [
    v.colorId,
    {
      id: v.colorId,
      name: v.name,
      hex: v.hex ?? null,
      imageUrl: v.imageUrl || "",
      stock: v.stock || 0,
      rate: pricedPerItem ? (v.rate ?? form.rate ?? 0) : null,
    },
  ])
);

/* Add portions with portionId field */
form.portions
  .filter((p) => p.name.trim())
  .forEach((p) => {
    variantsForSave[p.id] = {
      id: p.id,
      name: p.name,
      imageUrl: "",
      stock: p.stock || 0,
      rate: pricedPerItem ? p.rate : null,
    };
  });
const payload: any = {
  ...form,
  vertical: activeVertical,
  extras: form.extras.filter((e) => e.name.trim()),
  variants: variantsForSave,
  rate: pricedPerItem ? null : form.rate,
  isDraft: !form.isPublished,
};
delete payload.photos;
delete payload.videoUrl;

/* PASS 1: Save the food item */
const saved = await dispatch(
  upsertFoodItem({ listingId: editingId || undefined, brandId, data: payload })
).unwrap();

/* PASS 2: Upload variant photos that have a .file */
const withFiles = variantList.filter((v) => v.previewUrl && !v.imageUrl);
if (withFiles.length) {
  setVariantUploading(""); // show uploading state
  const uploaded: Record<string, string> = {};
  for (const v of withFiles) {
    try {
      /* Generate a fake File from previewUrl for upload */
      const blob = await fetch(v.previewUrl as string).then((r) => r.blob());
      const file = new File([blob], `variant-${v.colorId}.jpg`, { type: "image/jpeg" });
      
      const res = await uploadFiles([file], "photo");
      const url = res.downloadURLs?.[0];
      if (url) uploaded[v.colorId] = url;
    } catch (err) {
      console.error(`Failed to upload variant ${v.colorId}:`, err);
    }
  }

  /* PASS 3: Rewrite variants with real URLs */
  if (Object.keys(uploaded).length) {
    Object.entries(uploaded).forEach(([cid, url]) => {
      variantsForSave[cid].imageUrl = url;
    });
    await dispatch(
      upsertFoodItem({ listingId: saved.listingId, brandId, data: { variants: variantsForSave } })
    ).unwrap();

    /* Update form to show real URLs, clean up previews */
    setForm((f) => {
      const variants = { ...f.variants };
      Object.entries(uploaded).forEach(([cid, url]) => {
        if (variants[cid]) {
          if (variants[cid].previewUrl) URL.revokeObjectURL(variants[cid].previewUrl as string);
          variants[cid] = { ...variants[cid], imageUrl: url, previewUrl: undefined };
        }
      });
      return { ...f, variants };
    });
  }
  setVariantUploading(null);
}

      setToast(editingId ? "Saved" : `Added to your ${cfg.items}`);
      navigate(base);
    } catch (err: any) {
      setToast(err?.message || "Could not save — check the fields and try again");
    } finally {
      setSaving(false);
    }
  };

  /* ── delete ── */
  const handleDelete = async (id: string) => {
    await dispatch(deleteFoodItem(id));
    setConfirmDeleteId(null);
    setToast("Deleted");
    if (view === "form" && editingId === id) navigate(base);
  };

  /* ── media ── */
  const uploadFiles = async (files: File[], type: "photo" | "video") => {
    const result = await dispatch(
      upsertFoodMedia({ listingId: editingId || undefined, brandId, files, type })
    ).unwrap();
    if (!editingId && result.listingId) {
      setEditingId(result.listingId);
      navigate(`${base}/${result.listingId}`, { replace: true });
    }
    return result;
  };

  const handlePhotos = async (files: FileList | null) => {
    if (!files || !files.length) return;
    setUploading(true);
    try {
      const result = await uploadFiles(Array.from(files), "photo");
      setForm((f) => ({ ...f, photos: [...f.photos, ...result.downloadURLs] }));
      setToast("Photos uploaded");
    } catch (err: any) {
      setToast(err?.message || "Upload failed — try a smaller file");
    } finally {
      setUploading(false);
    }
  };

  const handleVideo = async (files: FileList | null) => {
    if (!files || !files.length) return;
    setUploadingVideo(true);
    try {
      const result = await uploadFiles([files[0]], "video");
      setForm((f) => ({ ...f, videoUrl: result.downloadURLs[0] || "" }));
      setToast("Video uploaded");
    } catch (err: any) {
      setToast(err?.message || "Video upload failed");
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleVariantImage = async (colorId: string, file: File) => {
    /* Show it straight away, swap in the stored URL when it lands. */
    const preview = URL.createObjectURL(file);
    updateVariant(colorId, { previewUrl: preview });
    setVariantUploading(colorId);
    try {
      const result = await uploadFiles([file], "photo");
      const url = result.downloadURLs?.[0];
      if (url) updateVariant(colorId, { imageUrl: url, previewUrl: undefined });
    } catch (err: any) {
      updateVariant(colorId, { previewUrl: undefined });
      setToast(err?.message || "Colour photo failed to upload");
    } finally {
      setVariantUploading(null);
    }
  };

  const removePhoto = async (url: string) => {
    setForm((f) => ({ ...f, photos: f.photos.filter((p) => p !== url) }));
    if (editingId) await dispatch(deleteFoodPhoto({ listingId: editingId, photoUrl: url }));
  };

  const priceSummary = `${money(form.rate)} · ${form.stock} left`;

  /* ── list columns, per counter ── */
  const columns = useMemo(() => {
    const cols = ["44px", "2.2fr", "1fr", ".8fr", ".9fr", "1.4fr"];
    if (cfg.showKitchen) cols.push(".8fr", ".8fr");
    if (cfg.showFreshness) cols.push("1fr");
    cols.push("1fr");
    return cols.join(" ");
  }, [cfg]);

  /* ═══════════════════════════════════════════════════════════
     LIST VIEW
  ═══════════════════════════════════════════════════════════ */
  if (view === "list") {
    return (
      <div className="fs-root fs-fade" style={rootStyle}>
        <style>{FS_CSS}</style>

        <div className="fs-topbar">
          <div className="fs-topbar-a">
            <div className="fs-tabs">
              {(["all", "live", "draft"] as const).map((k) => (
                <button
                  key={k}
                  className={`fs-tab${statusFilter === k ? " active" : ""}`}
                  onClick={() => setStatusFilter(k)}
                >
                  {k === "all" ? "Everything" : k === "live" ? cfg.liveTab : "Drafts"}
                  <span className="fs-num">{counts[k]}</span>
                </button>
              ))}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <span className="fs-counter">{cfg.icon} {cfg.key === "fastfood" ? "Fast food" : cfg.key}</span>
              <button
                className="fs-btn fs-btn-primary"
                style={{ padding: "10px 18px", display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}
                onClick={openNew}
              >
                <Plus size={15} /> {cfg.newLabel}
              </button>
            </div>
          </div>
          <div className="fs-topbar-b">
            <div className="fs-searchbar" style={{ flex: 1 }}>
              <Search size={14} color={HEART.textFaint} />
              <input placeholder={cfg.searchLabel} value={search} onChange={(e) => setSearch(e.target.value)} />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  style={{ background: HEART.border, border: "none", borderRadius: 999, width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: HEART.textMuted }}
                >
                  <X size={11} />
                </button>
              )}
            </div>
          </div>
        </div>

        {brandFoodLoading ? (
          <div className="fs-table" style={{ padding: 16 }}>
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="fs-skeleton" style={{ height: 54, borderRadius: 12, marginBottom: 10 }} />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="fs-card fs-empty">
            <div style={{ width: 56, height: 56, borderRadius: 16, background: cfg.accentSoft, color: cfg.accentDark, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 14 }}>
              {React.cloneElement(cfg.icon as React.ReactElement)}
            </div>
            <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 700, margin: "0 0 6px" }}>
              {scopedItems.length === 0 ? cfg.emptyTitle : "No matches"}
            </p>
            <p style={{ fontSize: 13, color: HEART.textMuted, maxWidth: 330, margin: "0 0 18px" }}>
              {scopedItems.length === 0 ? cfg.emptyBody : "Change the search or switch tabs to see more."}
            </p>
            {scopedItems.length === 0 && (
              <button
                className="fs-btn fs-btn-primary"
                style={{ padding: "10px 20px", display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}
                onClick={openNew}
              >
                <Plus size={15} /> {cfg.newLabel}
              </button>
            )}
          </div>
        ) : (
          <div className="fs-table">
            <div className="fs-tscroll">
              <div className="fs-tgrid">
                <div className="fs-thead" style={{ gridTemplateColumns: columns }}>
                  <div
                    className={`fs-check${filtered.length > 0 && selectedIds.size === filtered.length ? " on" : ""}`}
                    onClick={toggleSelectAll}
                    title="Select all"
                  >
                    {filtered.length > 0 && selectedIds.size === filtered.length && <Check size={13} />}
                  </div>
                  <div className="fs-th">Item</div>
                  <div className="fs-th">Status</div>
                  <div className="fs-th">Left</div>
                  <div className="fs-th">Price</div>
                  <div className="fs-th">Category</div>
                  {cfg.showKitchen && <div className="fs-th">Kcal</div>}
                  {cfg.showKitchen && <div className="fs-th">Prep</div>}
                  {cfg.showFreshness && <div className="fs-th">Best before</div>}
                  <div className="fs-th">Updated</div>
                </div>

                {filtered.map((item) => {
                  const raw: any = item;
                  const isSel = selectedIds.has(item.listingId);
                  const out = (item.stock ?? 0) <= 0 || item.available === false;
                  const fmtDate = (v: any) => {
                    if (!v) return "—";
                    const d = v?.seconds ? new Date(v.seconds * 1000) : new Date(v);
                    return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
                  };
                  return (
                    <div
                      key={item.listingId}
                      className={`fs-trow${isSel ? " sel" : ""}`}
                      style={{ gridTemplateColumns: columns }}
                      onClick={() => openEdit(item)}
                    >
                      <div className={`fs-check${isSel ? " on" : ""}`} onClick={(e) => toggleSelect(item.listingId, e)}>
                        {isSel && <Check size={13} />}
                      </div>

                      <div className="fs-titem">
                        <div className="fs-tthumb">
                          {item.photos && item.photos[0] ? (
                            <img src={item.photos[0]} alt={item.name} />
                          ) : (
                            <ImageIcon size={15} color={HEART.textFaint} />
                          )}
                        </div>
                        <span className="fs-tname">{item.name || "Untitled"}</span>
                      </div>

                      <div>
                        <span
                          className={`fs-pill ${
                            item.available === false ? "fs-pill-off" : item.isPublished ? "fs-pill-live" : "fs-pill-draft"
                          }`}
                        >
                          {item.available === false ? "Sold out" : item.isPublished ? cfg.liveWord : "Draft"}
                        </span>
                      </div>

                      <div className="fs-tcell">
                        <span className="fs-num">{out ? 0 : item.stock}</span>
                      </div>

                      <div className="fs-tcell">
                        <Money value={item.rate} />
                        <span style={{ color: HEART.textFaint, fontWeight: 500 }}>
                          {" "}{RATE_LABEL[(raw.rateType as RateType) || DEFAULT_RATE_TYPE]}
                        </span>
                      </div>

                      <div className="fs-tcell muted">
                        {raw.categoryId ? pathLabel(String(raw.categoryId)).split(" › ").slice(1).join(" › ") || item.category : item.category || "Uncategorised"}
                      </div>

                      {cfg.showKitchen && (
                        <div className="fs-tcell">
                          {item.calories ? <span className="fs-num">{item.calories}</span> : <span className="fs-tcell muted">—</span>}
                        </div>
                      )}

                      {cfg.showKitchen && (
                        <div className="fs-tcell">
                          {item.prepTimeMinutes ? (
                            <>
                              <span className="fs-num">{item.prepTimeMinutes}</span>
                              <span style={{ color: HEART.textFaint, fontWeight: 500 }}> min</span>
                            </>
                          ) : (
                            <span className="fs-tcell muted">—</span>
                          )}
                        </div>
                      )}

                      {cfg.showFreshness && (
                        <div className="fs-tcell muted">{raw.bestBefore || "—"}</div>
                      )}

                      <div className="fs-tcell muted">{fmtDate(raw.updatedAt)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {selectedIds.size > 0 && (
          <div className="fs-bulk fs-fade">
            <span style={{ fontSize: 12.5, fontWeight: 800, marginRight: 4 }}>
              <span style={{ color: cfg.accent }}>{selectedIds.size}</span> selected
            </span>
            <button className="fs-bulk-btn" onClick={() => bulkPublish(true)}>Publish</button>
            <button className="fs-bulk-btn" onClick={() => bulkPublish(false)}>Move to drafts</button>
            <button className="fs-bulk-btn" onClick={() => bulkAvailability(false)}>Mark sold out</button>
            <button className="fs-bulk-btn" onClick={() => bulkAvailability(true)}>Make available</button>
            <button className="fs-bulk-btn danger" onClick={bulkDelete}>Delete</button>
            <button className="fs-bulk-btn" onClick={() => setSelectedIds(new Set())}>Clear</button>
          </div>
        )}

        {confirmDeleteId && (
          <>
            <div className="fs-modal-backdrop" onClick={() => setConfirmDeleteId(null)} />
            <div className="fs-modal" style={{ textAlign: "center" }}>
              <h3 style={{ marginBottom: 8 }}>Delete this {cfg.item}?</h3>
              <p style={{ fontSize: 12.5, color: HEART.textMuted, margin: "0 0 20px" }}>
                It comes down straight away and can't be brought back.
              </p>
              <div style={{ display: "flex", gap: 10 }}>
                <button className="fs-btn fs-btn-ghost" style={{ flex: 1, padding: "11px 0" }} onClick={() => setConfirmDeleteId(null)}>Keep it</button>
                <button className="fs-btn fs-btn-danger" style={{ flex: 1, padding: "11px 0" }} onClick={() => handleDelete(confirmDeleteId)}>Delete</button>
              </div>
            </div>
          </>
        )}

        {toast && <div className="fs-toast fs-slide">{toast}</div>}
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════
     FORM VIEW
  ═══════════════════════════════════════════════════════════ */
  return (
    <div className="fs-root fs-fade" style={rootStyle}>
      <style>{FS_CSS}</style>

      <div className="fs-formhead">
        <div style={{ display: "flex", alignItems: "center", gap: 11, minWidth: 0 }}>
          <button
            className="fs-btn fs-btn-ghost"
            style={{ width: 34, height: 34, display: "flex", alignItems: "center", justifyContent: "center", padding: 0, flexShrink: 0 }}
            onClick={backToList}
            title="Back"
          >
            <ChevronLeft size={17} />
          </button>
          <div style={{ minWidth: 0 }}>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 24, fontWeight: 700, margin: 0, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
              {editingId ? form.name || `Edit ${cfg.item}` : cfg.newLabel}
            </h1>
            <p style={{ fontSize: 12, color: HEART.textFaint, margin: "2px 0 0", display: "flex", alignItems: "center", gap: 8 }}>
              {editingId && (
                <>
                  <Money value={form.rate} /> · <span className="fs-num">{form.stock}</span> left
                </>
              )}
            </p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <span className="fs-counter">{cfg.icon} {cfg.key === "fastfood" ? "Fast food" : cfg.key}</span>
          <button className="fs-btn fs-btn-ghost" style={{ padding: "10px 18px", fontSize: 13 }} onClick={backToList} disabled={saving}>
            Discard
          </button>
          <button
            className="fs-btn fs-btn-primary"
            style={{ padding: "10px 22px", fontSize: 13, display: "flex", alignItems: "center", gap: 8 }}
            onClick={handleSave}
            disabled={saving}
          >
            {saving && (
              <span className="fs-spin" style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,.4)", borderTopColor: "#fff", borderRadius: "50%" }} />
            )}
            Save
          </button>
        </div>
      </div>

      <div className="fs-formgrid" style={{ display: "grid", gridTemplateColumns: "1fr 330px", gap: 20, alignItems: "start" }}>
        {/* ── MAIN COLUMN ── */}
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {/* the item */}
          <div className="fs-card">
            <div className="fs-cardhead">
              <div className="fs-cardic hot">{cfg.icon}</div>
              <p className="fs-cardtitle" style={{ margin: 0 }}>{cfg.detailsTitle}</p>
            </div>

            <label className="fs-label">Name</label>
            <input
              className="fs-input"
              placeholder={cfg.key === "farm" ? "Vine tomatoes" : cfg.key === "grocery" ? "Semi-skimmed milk 1L" : "Crispy chicken burger"}
              value={form.name}
              onChange={(e) => set("name", e.target.value)}
            />

            <hr className="fs-divider" />

            <label className="fs-label">What's in it</label>
            <RichTextEditor
              value={form.description}
              onChange={setDescription}
              resetKey={editingId}
              placeholder={
                cfg.key === "farm"
                  ? "Variety, how it was grown, when it was picked…"
                  : cfg.key === "grocery"
                  ? "Ingredients, storage, what's in the pack…"
                  : "Ingredients, sides it comes with, how it's served…"
              }
            />

            <hr className="fs-divider" />

            <label className="fs-label">Photos</label>
            {form.photos.length > 0 && (
              <div className="fs-mediagrid" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 10, marginBottom: 12 }}>
                {form.photos.map((url, i) => (
                  <div key={url} className="fs-thumb">
                    <img src={url} alt="" />
                    {i === 0 && <span className="fs-thumb-cover">Cover</span>}
                    <button className="fs-thumb-del" onClick={() => removePhoto(url)} aria-label="Remove photo">
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div
              className={`fs-drop${dragging ? " dragging" : ""}`}
              onClick={() => photoInputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => { e.preventDefault(); setDragging(false); handlePhotos(e.dataTransfer.files); }}
            >
              {uploading ? (
                <>
                  <span className="fs-spin" style={{ width: 22, height: 22, border: `2.5px solid ${HEART.border}`, borderTopColor: cfg.accent, borderRadius: "50%" }} />
                  <span style={{ fontSize: 12.5, fontWeight: 700, color: HEART.textMuted }}>Uploading…</span>
                </>
              ) : (
                <>
                  <ImageIcon size={22} color={cfg.accentDark} />
                  <span style={{ fontSize: 12.5, fontWeight: 700 }}>Drop photos here, or click to upload</span>
                  <span style={{ fontSize: 11, color: HEART.textFaint }}>First photo becomes the cover. JPG, PNG or WEBP.</span>
                </>
              )}
            </div>
            <input ref={photoInputRef} type="file" accept="image/*" multiple hidden onChange={(e) => handlePhotos(e.target.files)} />

            <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
              <button
                className="fs-btn fs-btn-ghost"
                style={{ padding: "10px 16px", fontSize: 12.5, display: "flex", alignItems: "center", gap: 8 }}
                onClick={() => videoInputRef.current?.click()}
                disabled={uploadingVideo}
              >
                <Video size={14} /> {uploadingVideo ? "Uploading video…" : form.videoUrl ? "Replace video" : "Add a video"}
              </button>
              {form.videoUrl && (
                <>
                  <a href={form.videoUrl} target="_blank" rel="noopener" style={{ fontSize: 12, color: HEART.textMuted }}>
                    Preview
                  </a>
                  <button
                    className="fs-btn fs-btn-ghost"
                    style={{ padding: "10px 14px", fontSize: 12.5 }}
                    onClick={() => set("videoUrl", "")}
                  >
                    Remove
                  </button>
                </>
              )}
            </div>
            <input ref={videoInputRef} type="file" accept="video/*" hidden onChange={(e) => handleVideo(e.target.files)} />

            <hr className="fs-divider" />

            <label className="fs-label">Category</label>
            <CategoryPicker
              value={form.categoryId}
              onChange={(id, label) =>
                /* A new category means a new field set — old answers wouldn't fit. */
                setForm((f) => ({ ...f, categoryId: id, category: label, attributes: {}, variants: {} }))
              }
              isMobile={isMobile}
              roots={cfg.roots}
              title={cfg.pickLabel}
              rootId={scopeId || undefined}
            />
            <p className="fs-hint">
              {scopeId ? "Opens inside this section. Use the breadcrumb to step back out." : cfg.pickHint}
            </p>
          </div>

          {/* attributes — entirely category-driven */}
          <AttributesRenderer
            attrs={attrs}
            values={form.attributes}
            onChange={(attrId, value) => set("attributes", { ...form.attributes, [attrId]: value })}
            isMobile={isMobile}
            title={`${NODES.get(form.categoryId)?.name ?? "Category"} details`}
            colorAttrId={colorAttrId}
            onToggleColor={toggleColor}
          />

          {/* per-colour rows — only exist once a colour is picked */}
          {hasVariants && (
            <div className="fs-card fs-fade">
              <div className="fs-cardhead" style={{ marginBottom: 12 }}>
                <div className="fs-cardic"><ImageIcon size={15} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="fs-cardtitle" style={{ margin: 0 }}>By colour</p>
                  <p className="fs-hint" style={{ marginTop: 2 }}>
                    A photo, a quantity and a price for each one. Prices start on the base price of {money(form.rate)}.
                  </p>
                </div>
              </div>
              {variantList.map((v) => (
                <VariantCard
                  key={v.colorId}
                  variant={v}
                  baseRate={form.rate}
                  rateType={form.rateType}
                  uploading={variantUploading === v.colorId}
                  onUpdate={updateVariant}
                  onPickImage={handleVariantImage}
                  onRemove={toggleColor}
                />
              ))}
            </div>
          )}

          {/* price & stock */}
          {isMobile ? (
            <button
              className="fs-card"
              style={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer", textAlign: "left" }}
              onClick={() => setPriceSheetOpen(true)}
            >
              <div>
                <p className="fs-cardtitle" style={{ margin: "0 0 4px" }}>Price & stock</p>
                <p style={{ fontSize: 12, color: HEART.textMuted, margin: 0 }}>{priceSummary}</p>
              </div>
              <ChevronDown size={16} style={{ transform: "rotate(-90deg)", color: HEART.textFaint }} />
            </button>
          ) : (
            <div className="fs-card">
              <div className="fs-cardhead">
                <div className="fs-cardic"><Scale size={15} /></div>
                <p className="fs-cardtitle" style={{ margin: 0 }}>Price & stock</p>
              </div>
              <PriceStockFields form={form} cfg={cfg} hasVariants={hasVariants} set={set} />
            </div>
          )}

          {/* sizes / packs / units of sale */}
          <div className="fs-card">
            <div className="fs-cardhead" style={{ marginBottom: 12 }}>
              <div className="fs-cardic"><Ruler size={15} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="fs-cardtitle" style={{ margin: 0 }}>{cfg.sizesTitle}</p>
                <p className="fs-hint" style={{ marginTop: 2 }}>{cfg.sizesHint}</p>
              </div>
              <button
                className="fs-btn fs-btn-ghost"
                style={{ padding: "8px 14px", fontSize: 12.5, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}
                onClick={addPortion}
              >
                <Plus size={13} /> Add row
              </button>
            </div>
            <PortionsSection
              portions={form.portions}
              isMobile={isMobile}
              cfg={cfg}
              onUpdate={updatePortion}
              onRemove={removePortion}
            />
          </div>

          {/* extras — fast food only */}
          {cfg.showExtras && (
            <div className="fs-card">
              <div className="fs-cardhead" style={{ marginBottom: 12 }}>
                <div className="fs-cardic"><Plus size={15} /></div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p className="fs-cardtitle" style={{ margin: 0 }}>Extras</p>
                  <p className="fs-hint" style={{ marginTop: 2 }}>
                    Paid add-ons diners can tick at checkout — sauces, toppings, sides.
                  </p>
                </div>
                <button
                  className="fs-btn fs-btn-ghost"
                  style={{ padding: "8px 14px", fontSize: 12.5, display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}
                  onClick={addExtra}
                >
                  <Plus size={13} /> Add extra
                </button>
              </div>
              <ExtrasSection
                extras={form.extras}
                isMobile={isMobile}
                onUpdate={updateExtra}
                onRemove={removeExtra}
              />
            </div>
          )}

          {/* allergens — typed by the seller, no preset list */}
          <div className="fs-card">
            <div className="fs-cardhead" style={{ marginBottom: 12 }}>
              <div className="fs-cardic"><Tag size={15} /></div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p className="fs-cardtitle" style={{ margin: 0 }}>Allergens</p>
                <p className="fs-hint" style={{ marginTop: 2 }}>Shown as a warning under the {cfg.item}.</p>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: form.allergens.length ? 12 : 0 }}>
              <input
                className="fs-input"
                placeholder="e.g. peanuts"
                value={allergenDraft}
                onChange={(e) => setAllergenDraft(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAllergen(); } }}
              />
              <button className="fs-btn fs-btn-ghost" style={{ padding: "0 18px", fontSize: 12.5, flexShrink: 0 }} onClick={addAllergen}>
                Add
              </button>
            </div>
            {form.allergens.length > 0 && (
              <div className="fs-chips">
                {form.allergens.map((a) => (
                  <span key={a} className="fs-chip on">
                    {a}
                    <button
                      className="fs-chip-del"
                      onClick={() => set("allergens", form.allergens.filter((x) => x !== a))}
                      aria-label={`Remove ${a}`}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* kitchen — fast food only */}
          {cfg.showKitchen && (
            <div className="fs-card">
              <div className="fs-cardhead">
                <div className="fs-cardic"><Timer size={15} /></div>
                <p className="fs-cardtitle" style={{ margin: 0 }}>Kitchen</p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                <div>
                  <label className="fs-label">Prep time</label>
                  <NumberStepper value={form.prepTimeMinutes} onChange={(n) => set("prepTimeMinutes", n)} step={1} max={240} suffix="min" />
                  <p className="fs-hint">Shown to customers as the wait before pickup.</p>
                </div>
                <div>
                  <label className="fs-label">Calories</label>
                  <NumberStepper value={form.calories} onChange={(n) => set("calories", n)} step={10} max={5000} suffix="kcal" />
                  <p className="fs-hint">Leave at 0 to hide it on the menu.</p>
                </div>
              </div>
            </div>
          )}

          {/* freshness — grocery & farm */}
          {cfg.showFreshness && (
            <div className="fs-card">
              <div className="fs-cardhead">
                <div className="fs-cardic"><Snowflake size={15} /></div>
                <p className="fs-cardtitle" style={{ margin: 0 }}>Freshness</p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14, marginBottom: 14 }}>
                <div>
                  <label className="fs-label">Shelf life</label>
                  <NumberStepper value={form.shelfLifeDays} onChange={(n) => set("shelfLifeDays", n)} step={1} max={3650} suffix="days" />
                </div>
                <div>
                  <label className="fs-label">Batch code</label>
                  <input className="fs-input" placeholder="e.g. B-2411-07" value={form.batchCode} onChange={(e) => set("batchCode", e.target.value)} />
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
                {cfg.showHarvest && (
                  <div>
                    <label className="fs-label">Harvested on</label>
                    <input className="fs-input" type="date" value={form.harvestedOn} onChange={(e) => set("harvestedOn", e.target.value)} />
                  </div>
                )}
                <div>
                  <label className="fs-label">Best before</label>
                  <input className="fs-input" type="date" value={form.bestBefore} onChange={(e) => set("bestBefore", e.target.value)} />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── SIDEBAR ── */}
        <div className="fs-sidebar" style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <div className="fs-card">
            <p className="fs-cardtitle">Status</p>
            <select
              className="fs-select"
              value={form.isPublished ? "live" : "draft"}
              onChange={(e) => set("isPublished", e.target.value === "live")}
            >
              <option value="draft">Draft</option>
              <option value="live">{cfg.liveTab}</option>
            </select>
            <p className="fs-hint">
              {form.isPublished
                ? "Customers can order this as soon as you save."
                : `Only you can see this until you put it ${cfg.liveTab.toLowerCase()}.`}
            </p>

            <hr className="fs-divider" style={{ margin: "18px 0" }} />

            <div className="fs-togglerow" style={{ borderTop: "none", paddingTop: 0 }}>
              <span className="fs-togglelabel">Available right now</span>
              <button className={`fs-toggle${form.available ? " on" : ""}`} onClick={() => set("available", !form.available)} aria-label="Available right now">
                <span />
              </button>
            </div>
          </div>

          {/* at a glance */}
          <div className="fs-ticket">
            <p className="fs-cardtitle" style={{ marginBottom: 10 }}>At a glance</p>

            <div className="fs-ticketrow">
              <span className="fs-ticketkey">Base price</span>
              {form.rateType === "perItem" && hasVariants
                ? <span style={{ fontWeight: 700 }}>Set per colour</span>
                : <Money value={form.rate} large />}
            </div>
            <div className="fs-ticketrow">
              <span className="fs-ticketkey">Charged</span>
              <span style={{ fontWeight: 700 }}>{RATE_COPY[form.rateType].title}</span>
            </div>
            <div className="fs-ticketrow">
              <span className="fs-ticketkey">{cfg.stockLabel}</span>
              <span className="fs-num fs-num-lg">{form.stock}</span>
            </div>
            {form.categoryId && (
              <div className="fs-ticketrow">
                <span className="fs-ticketkey">Category</span>
                <span style={{ fontWeight: 700, textAlign: "right" }}>{NODES.get(form.categoryId)?.name}</span>
              </div>
            )}
            {cfg.showKitchen && (
              <>
                <div className="fs-ticketrow">
                  <span className="fs-ticketkey">Prep</span>
                  <span><span className="fs-num">{form.prepTimeMinutes || 0}</span> min</span>
                </div>
                <div className="fs-ticketrow">
                  <span className="fs-ticketkey">Calories</span>
                  <span><span className="fs-num">{form.calories || 0}</span> kcal</span>
                </div>
              </>
            )}
            {variantList.map((v) => (
              <div className="fs-ticketrow" key={v.colorId}>
                <span className="fs-ticketkey">{v.name}</span>
                <span>
                  <span className="fs-num">{v.stock}</span>
                  <span style={{ color: HEART.textFaint, fontWeight: 500 }}> · {money(v.rate ?? form.rate)}</span>
                </span>
              </div>
            ))}
            {form.portions.filter((p) => p.name.trim()).map((p) => (
              <div className="fs-ticketrow" key={p.id}>
                <span className="fs-ticketkey">{p.name}{p.unit ? ` · ${p.unit}` : ""}</span>
                <span className="fs-num">{p.rate > 0 ? `+${money(p.rate)}` : money(0)}</span>
              </div>
            ))}
            {cfg.showExtras && form.extras.filter((e) => e.name.trim()).length > 0 && (
              <div className="fs-ticketrow">
                <span className="fs-ticketkey">Extras</span>
                <span><span className="fs-num">{form.extras.filter((e) => e.name.trim()).length}</span> available</span>
              </div>
            )}

            {form.compareAtPrice > form.rate && form.rate > 0 && (
              <p className="fs-hint">
                Shows as <span style={{ textDecoration: "line-through" }}>{money(form.compareAtPrice)}</span> struck through —
                a <span className="fs-num">{Math.round((1 - form.rate / form.compareAtPrice) * 100)}</span>% saving.
              </p>
            )}
          </div>

          {editingId && (
            <button
              className="fs-btn fs-btn-danger"
              style={{ padding: "12px 0", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}
              onClick={() => setConfirmDeleteId(editingId)}
            >
              <Trash2 size={14} /> Delete {cfg.item}
            </button>
          )}
        </div>
      </div>

      {/* mobile price & stock sheet */}
      {isMobile && priceSheetOpen && (
        <>
          <div className="fs-modal-backdrop" onClick={() => setPriceSheetOpen(false)} />
          <div className="fs-sheet">
            <div className="fs-sheet-handle"><GripHorizontal size={18} color={HEART.textFaint} /></div>
            <div className="fs-sheet-header">
              <p style={{ fontSize: 15, fontWeight: 800, margin: 0 }}>Price & stock</p>
              <button className="fs-btn fs-btn-primary" style={{ padding: "9px 18px", fontSize: 12.5 }} onClick={() => setPriceSheetOpen(false)}>
                Done
              </button>
            </div>
            <div className="fs-sheet-body">
              <PriceStockFields form={form} cfg={cfg} hasVariants={hasVariants} set={set} />
            </div>
          </div>
        </>
      )}

      {confirmDeleteId && (
        <>
          <div className="fs-modal-backdrop" onClick={() => setConfirmDeleteId(null)} />
          <div className="fs-modal" style={{ textAlign: "center" }}>
            <h3 style={{ marginBottom: 8 }}>Delete this {cfg.item}?</h3>
            <p style={{ fontSize: 12.5, color: HEART.textMuted, margin: "0 0 20px" }}>
              It comes down straight away and can't be brought back.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button className="fs-btn fs-btn-ghost" style={{ flex: 1, padding: "11px 0" }} onClick={() => setConfirmDeleteId(null)}>Keep it</button>
              <button className="fs-btn fs-btn-danger" style={{ flex: 1, padding: "11px 0" }} onClick={() => handleDelete(confirmDeleteId)}>Delete</button>
            </div>
          </div>
        </>
      )}

      {toast && <div className="fs-toast fs-slide">{toast}</div>}
    </div>
  );
};

export default FreshStore;